import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Search, Mic, MicOff, Send, Plus, Settings, Users, Database, Zap, CreditCard, BarChart3, FileText, Image, Video, File, Trash2, Download, Upload, Check, X, Edit2, Save, ChevronDown, ChevronRight, Menu, LogOut, Bell, HelpCircle, Globe, Folder, Link, MessageSquare, Clock, TrendingUp, AlertCircle, DollarSign, Activity, Shield } from 'lucide-react';
import apiService from './services/apiService.js';

export default function KnowledgeAIPlatform() {
  const [currentView, setCurrentView] = useState('chat');
  const [inputValue, setInputValue] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [isInputFocused, setIsInputFocused] = useState(false);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const chatInputRef = useRef(null);

  // ACL Authentication State
  const [authToken, setAuthToken] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [userDataSources, setUserDataSources] = useState([]);

  // ACL Functions
  const loginAsUser = async (username) => {
    try {
      const response = await fetch('http://localhost:3007/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username })
      });

      if (!response.ok) {
        throw new Error('Login failed');
      }

      const data = await response.json();
      setAuthToken(data.access_token);
      setCurrentUser(data.user);

      await loadUserDataSources(data.access_token);
    } catch (error) {
      alert('Login failed: ' + error.message);
    }
  };

  const loadUserDataSources = async (token) => {
    try {
      const response = await fetch('http://localhost:3007/api/data-sources', {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        throw new Error('Failed to load data sources');
      }

      const data = await response.json();
      setUserDataSources(data.data_sources);
    } catch (error) {
      console.error('Failed to load data sources:', error);
      setUserDataSources([]);
    }
  };

  const logout = () => {
    setAuthToken(null);
    setCurrentUser(null);
    setUserDataSources([]);
  };

  // Assistants State
  const [assistants, setAssistants] = useState([
    {
      id: 1,
      name: 'General Assistant',
      description: 'Access to all company data sources',
      icon: '🤖',
      color: 'blue',
      dataSources: [1, 2, 3, 5], // IDs of connected data sources
      isActive: true
    },
    {
      id: 2,
      name: 'Sales Assistant',
      description: 'CRM and sales data only',
      icon: '💼',
      color: 'green',
      dataSources: [1, 3],
      isActive: true
    },
    {
      id: 3,
      name: 'HR Assistant',
      description: 'HR documents and employee data',
      icon: '👥',
      color: 'purple',
      dataSources: [1, 2],
      isActive: true
    },
    {
      id: 4,
      name: 'Support Assistant',
      description: 'Customer support tickets and docs',
      icon: '🎧',
      color: 'orange',
      dataSources: [2, 3],
      isActive: false
    },
    {
      id: 5,
      name: 'Document Assistant',
      description: 'Chat with documents from monitored folders',
      icon: '📄',
      color: 'indigo',
      dataSources: [],
      isActive: true,
      isDocumentBot: true,
      folderPath: 'D:/projects/eye/documentrag/knowledgebaseai/backend/test-documents'
    }
  ]);

  const [currentAssistant, setCurrentAssistant] = useState(assistants[0]);
  const [messages, setMessages] = useState({
    1: [{ role: 'assistant', content: 'Hello! I\'m your General Assistant. Ask me anything about your connected data sources.', timestamp: new Date() }],
    2: [{ role: 'assistant', content: 'Hi! I\'m your Sales Assistant. I can help with CRM and sales data.', timestamp: new Date() }],
    3: [{ role: 'assistant', content: 'Hello! I\'m your HR Assistant. I can help with employee data and HR documents.', timestamp: new Date() }],
    4: [{ role: 'assistant', content: 'Hi! I\'m your Support Assistant. I can help with customer support queries.', timestamp: new Date() }],
    5: [{ role: 'assistant', content: 'Hello! I\'m your Document Assistant. To get started: 1) Go to Data Sources and configure "Document Folder" with your folder path, 2) Then edit me to connect to that data source. I\'ll help you chat with your documents!', timestamp: new Date() }]
  });
  const [editingAssistant, setEditingAssistant] = useState(null);

  // Visual Workflow Builder State
  const [showWorkflowBuilder, setShowWorkflowBuilder] = useState(false);
  const [workflowNodes, setWorkflowNodes] = useState([]);
  const [workflowConnections, setWorkflowConnections] = useState([]);
  const [selectedNode, setSelectedNode] = useState(null);
  const [draggedNode, setDraggedNode] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });

  // Organization & User State
  const [currentOrg, setCurrentOrg] = useState({
    name: 'Acme Corporation',
    plan: 'Professional',
    users: 24,
    storage: '156 GB',
    queries: 12450
  });

  // Document Chat State
  const [folderDocuments, setFolderDocuments] = useState(new Map()); // Map of assistantId -> documents
  const [isProcessingMessage, setIsProcessingMessage] = useState(false);
  const [documentStats, setDocumentStats] = useState(new Map()); // Map of assistantId -> stats
  const [dataSourceStats, setDataSourceStats] = useState(new Map()); // Map of dataSourceId -> real stats

  // Fetch document stats for document assistants and data sources
  const fetchDocumentStats = useCallback(async () => {
      for (const assistant of assistants) {
        if (assistant.isDocumentBot && assistant.folderPath) {
          try {
            const stats = await apiService.getStats(assistant.id);
            setDocumentStats(prev => new Map(prev.set(assistant.id, stats)));
          } catch (error) {
            console.error(`Failed to fetch stats for assistant ${assistant.id}:`, error);
          }
        }
      }
    }, [assistants]);

    const fetchDataSourceStats = useCallback(async () => {
      // Fetch stats for Document Folder data source (id: 6)
      try {
        // Find the document assistant that matches the folder path
        const documentAssistant = assistants.find(a => a.isDocumentBot && a.folderPath);
        if (documentAssistant) {
          const stats = await apiService.getStats(documentAssistant.id);
          setDataSourceStats(prev => new Map(prev.set(6, {
            files: stats.totalFiles,
            lastSync: stats.isMonitoring ? 'Just now' : 'Never',
            status: stats.isMonitoring ? 'connected' : 'disconnected'
          })));
        } else {
          // No document assistant configured, show default values
          setDataSourceStats(prev => new Map(prev.set(6, {
            files: 0,
            lastSync: 'Never',
            status: 'disconnected'
          })));
        }
      } catch (error) {
        console.error('Failed to fetch data source stats:', error);
      }
    }, [assistants]);

  useEffect(() => {
    fetchDocumentStats();
    fetchDataSourceStats();

    // Refresh stats every 30 seconds
    const interval = setInterval(() => {
      fetchDocumentStats();
      fetchDataSourceStats();
    }, 30000);
    return () => clearInterval(interval);
  }, [fetchDocumentStats, fetchDataSourceStats]);

  // Data Sources State
  const [dataSources, setDataSources] = useState([
    { id: 1, name: 'Google Drive', type: 'drive', status: 'connected', files: 1240, lastSync: '2 mins ago', icon: '📁',
      config: { folders: ['Marketing Materials', 'Product Docs', 'Sales'], syncFrequency: 'realtime', fileTypes: ['pdf', 'docx', 'xlsx', 'pptx'] }
    },
    { id: 2, name: 'Slack Workspace', type: 'slack', status: 'connected', files: 5620, lastSync: '5 mins ago', icon: '💬',
      config: { channels: ['#general', '#sales', '#support', '#engineering'], syncFrequency: 'hourly', includeThreads: true }
    },
    { id: 3, name: 'Confluence', type: 'confluence', status: 'connected', files: 340, lastSync: '1 hour ago', icon: '📚',
      config: { spaces: ['Engineering', 'Product', 'HR'], syncFrequency: 'daily', includeAttachments: true }
    },
    { id: 4, name: 'SharePoint', type: 'sharepoint', status: 'disconnected', files: 0, lastSync: 'Never', icon: '🗂️',
      config: { sites: [], syncFrequency: 'hourly', fileTypes: [] }
    },
    { id: 5, name: 'Notion', type: 'notion', status: 'syncing', files: 890, lastSync: 'Syncing...', icon: '📝',
      config: { workspaces: ['Company Wiki', 'Projects'], syncFrequency: 'realtime', includeDatabases: true }
    },
    { id: 6, name: 'Document Folder', type: 'folder', status: 'disconnected', files: 0, lastSync: 'Never', icon: '📂',
      config: { folderPath: '', syncFrequency: 'realtime', fileTypes: ['pdf', 'docx', 'txt', 'xlsx', 'jpg', 'png', 'webp'] }
    }
  ]);

  const [configuringSource, setConfiguringSource] = useState(null);
  const [syncLogs, setSyncLogs] = useState([
    { id: 1, source: 'Google Drive', action: 'Sync completed', status: 'success', files: 45, timestamp: '2 mins ago', details: 'Added 12 files, updated 33' },
    { id: 2, source: 'Slack Workspace', action: 'Sync completed', status: 'success', files: 128, timestamp: '5 mins ago', details: 'Indexed 128 new messages' },
    { id: 3, source: 'Confluence', action: 'Sync completed', status: 'success', files: 8, timestamp: '1 hour ago', details: 'Updated 8 pages' },
    { id: 4, source: 'Google Drive', action: 'Connection error', status: 'error', files: 0, timestamp: '3 hours ago', details: 'Token expired, re-authentication needed' }
  ]);

  // Workflows State
  const [workflows, setWorkflows] = useState([
    { 
      id: 1, 
      name: 'Smart Document Summarization', 
      trigger: 'User uploads document in chat', 
      action: 'Extract text → Generate summary → Return in chat', 
      status: 'active', 
      runs: 234,
      description: 'Automatically summarizes uploaded PDFs, DOCX, and other documents',
      category: 'Document Processing',
      avgTime: '3.2s',
      successRate: 98.5
    },
    { 
      id: 2, 
      name: 'Multi-Source Deep Search', 
      trigger: 'Query contains "search everywhere" or "find all"', 
      action: 'Search all connected sources → Rank by relevance → Compile results', 
      status: 'active', 
      runs: 892,
      description: 'Performs comprehensive search across all data sources with relevance scoring',
      category: 'Search Enhancement',
      avgTime: '2.8s',
      successRate: 96.2
    },
    { 
      id: 3, 
      name: 'Intelligent Query Routing', 
      trigger: 'Analyze query keywords and context', 
      action: 'Route to best assistant → Notify user → Execute query', 
      status: 'active', 
      runs: 1543,
      description: 'Automatically routes questions to the most appropriate assistant',
      category: 'Chat Enhancement',
      avgTime: '0.5s',
      successRate: 94.8
    },
    { 
      id: 4, 
      name: 'Auto-Citation Generator', 
      trigger: 'Every AI response', 
      action: 'Extract sources → Format citations → Append to response', 
      status: 'active', 
      runs: 3421,
      description: 'Adds proper citations and source links to all AI responses',
      category: 'Chat Enhancement',
      avgTime: '0.3s',
      successRate: 99.1
    },
    { 
      id: 5, 
      name: 'Suggested Follow-ups', 
      trigger: 'After each AI response', 
      action: 'Analyze context → Generate 3 follow-up questions → Display in chat', 
      status: 'active', 
      runs: 2156,
      description: 'Suggests relevant follow-up questions to continue the conversation',
      category: 'Chat Enhancement',
      avgTime: '1.1s',
      successRate: 97.3
    },
    { 
      id: 6, 
      name: 'Document Generator', 
      trigger: 'User says "create document" or "generate report"', 
      action: 'Extract requirements → Generate document → Save to Drive → Share link', 
      status: 'active', 
      runs: 167,
      description: 'Creates formatted documents based on chat conversations',
      category: 'Document Processing',
      avgTime: '5.6s',
      successRate: 95.7
    },
    { 
      id: 7, 
      name: 'Data Extraction Pipeline', 
      trigger: 'Upload invoice, receipt, or form', 
      action: 'OCR → Extract structured data → Save to spreadsheet → Confirm in chat', 
      status: 'active', 
      runs: 456,
      description: 'Extracts data from documents and saves to Google Sheets',
      category: 'Document Processing',
      avgTime: '4.2s',
      successRate: 93.4
    },
    { 
      id: 8, 
      name: 'Answer Caching', 
      trigger: 'User asks question', 
      action: 'Check cache → If found, return cached answer → Else query AI', 
      status: 'active', 
      runs: 5234,
      description: 'Caches frequently asked questions for instant responses',
      category: 'Performance',
      avgTime: '0.1s',
      successRate: 99.8
    },
    { 
      id: 9, 
      name: 'Slack Summary Bot', 
      trigger: 'User says "summarize this thread" with Slack link', 
      action: 'Fetch Slack thread → Summarize → Post summary in chat', 
      status: 'active', 
      runs: 289,
      description: 'Summarizes Slack conversations and threads',
      category: 'Integration',
      avgTime: '3.8s',
      successRate: 96.9
    },
    { 
      id: 10, 
      name: 'Meeting Notes Processor', 
      trigger: 'Upload meeting notes or transcript', 
      action: 'Extract action items → Assign owners → Create calendar events → Send notifications', 
      status: 'paused', 
      runs: 78,
      description: 'Processes meeting notes and creates actionable tasks',
      category: 'Productivity',
      avgTime: '6.3s',
      successRate: 91.2
    },
    { 
      id: 11, 
      name: 'Email Alert System', 
      trigger: 'Critical query or error detected', 
      action: 'Generate summary → Send email to admin → Log incident', 
      status: 'active', 
      runs: 45,
      description: 'Sends email notifications for important queries or issues',
      category: 'Notifications',
      avgTime: '1.5s',
      successRate: 100
    },
    { 
      id: 12, 
      name: 'Query Analytics Dashboard', 
      trigger: 'Every 100 queries', 
      action: 'Analyze patterns → Generate insights → Update dashboard → Notify admin', 
      status: 'active', 
      runs: 124,
      description: 'Analyzes query patterns and generates usage insights',
      category: 'Analytics',
      avgTime: '8.2s',
      successRate: 98.0
    }
  ]);

  const [editingWorkflow, setEditingWorkflow] = useState(null);

  // Users State
  const [users, setUsers] = useState([
    { id: 1, name: 'Sarah Johnson', email: 'sarah@acme.com', role: 'Admin', queries: 1240, lastActive: '5 mins ago', status: 'online', groupIds: [1, 5] },
    { id: 2, name: 'Mike Chen', email: 'mike@acme.com', role: 'Member', queries: 890, lastActive: '1 hour ago', status: 'online', groupIds: [2, 3] },
    { id: 3, name: 'Emily Rodriguez', email: 'emily@acme.com', role: 'Member', queries: 645, lastActive: '3 hours ago', status: 'offline', groupIds: [2] },
    { id: 4, name: 'James Wilson', email: 'james@acme.com', role: 'Viewer', queries: 234, lastActive: 'Yesterday', status: 'offline', groupIds: [4] }
  ]);

  // User Groups State
  const [userGroups, setUserGroups] = useState([
    {
      id: 1,
      name: 'Administrators',
      description: 'System administrators with full access',
      color: 'red',
      icon: '👑',
      memberCount: 1,
      members: ['Sarah Johnson']
    },
    {
      id: 2,
      name: 'Sales Team',
      description: 'Sales and customer relations',
      color: 'green',
      icon: '💼',
      memberCount: 2,
      members: ['Mike Chen', 'Emily Rodriguez']
    },
    {
      id: 3,
      name: 'Engineering',
      description: 'Development and technical team',
      color: 'blue',
      icon: '⚙️',
      memberCount: 1,
      members: ['Mike Chen']
    },
    {
      id: 4,
      name: 'External Consultants',
      description: 'External contractors and consultants',
      color: 'gray',
      icon: '🔗',
      memberCount: 1,
      members: ['James Wilson']
    },
    {
      id: 5,
      name: 'HR Department',
      description: 'Human resources and people operations',
      color: 'purple',
      icon: '👥',
      memberCount: 1,
      members: ['Sarah Johnson']
    }
  ]);

  // Billing State
  const [billingData, setBillingData] = useState({
    currentPlan: 'Professional',
    monthlyQueries: 12450,
    queryLimit: 50000,
    storage: 156,
    storageLimit: 500,
    currentBill: 299,
    nextBilling: 'Nov 15, 2025',
    paymentMethod: '•••• 4242'
  });

  // Query History State
  const [queryHistory, setQueryHistory] = useState([
    { id: 1, user: 'Sarah Johnson', query: 'What were the Q3 sales figures?', timestamp: '2 mins ago', assistant: 'General Assistant', sources: ['Drive', 'Sheets'], tokens: 456 },
    { id: 2, user: 'Mike Chen', query: 'Show me the latest product roadmap', timestamp: '15 mins ago', assistant: 'General Assistant', sources: ['Confluence'], tokens: 892 },
    { id: 3, user: 'Emily Rodriguez', query: 'Find all customer feedback from last month', timestamp: '1 hour ago', assistant: 'Sales Assistant', sources: ['Slack', 'Drive'], tokens: 1203 }
  ]);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  // Only scroll when new messages are added, not on every messages object change
  const currentMessagesLength = useRef(0);
  useEffect(() => {
    const totalMessages = Object.values(messages).flat().length;
    if (totalMessages > currentMessagesLength.current) {
      scrollToBottom();
      currentMessagesLength.current = totalMessages;
    }
  }, [messages, scrollToBottom]);

  // Focus preservation effect
  useEffect(() => {
    if (isInputFocused && chatInputRef.current && document.activeElement !== chatInputRef.current) {
      chatInputRef.current.focus();
    }
  });

  const handleSendMessage = async () => {
    if (!inputValue.trim() || isProcessing) return;

    const userMessage = { role: 'user', content: inputValue, timestamp: new Date() };
    setMessages(prev => ({
      ...prev,
      [currentAssistant.id]: [...(prev[currentAssistant.id] || []), userMessage]
    }));

    const queryLower = inputValue.toLowerCase();
    const originalInput = inputValue;
    setInputValue('');
    setIsProcessing(true);

    // Handle Document Assistant differently
    if (currentAssistant.isDocumentBot) {
      try {
        // Check if folder is configured
        if (!currentAssistant.folderPath) {
          const errorResponse = {
            role: 'assistant',
            content: 'Please configure a folder path first. Click the settings icon to set up a folder to monitor.',
            timestamp: new Date()
          };
          setMessages(prev => ({
            ...prev,
            [currentAssistant.id]: [...(prev[currentAssistant.id] || []), errorResponse]
          }));
          setIsProcessing(false);
          return;
        }

        // Get conversation history for context
        const conversationHistory = messages[currentAssistant.id]
          ?.filter(msg => msg.role === 'user' || msg.role === 'assistant')
          ?.map(msg => ({ role: msg.role, content: msg.content })) || [];

        // Get AI response from backend
        const response = await apiService.chatWithDocuments(
          currentAssistant.id,
          originalInput,
          conversationHistory
        );

        const aiResponse = {
          role: 'assistant',
          content: response.answer,
          timestamp: new Date(),
          citations: response.citations,
          sources: response.citations?.map(citation => citation.source.split(/[\\/]/).pop()) || []
        };

        setMessages(prev => ({
          ...prev,
          [currentAssistant.id]: [...(prev[currentAssistant.id] || []), aiResponse]
        }));

      } catch (error) {
        console.error('Document chat error:', error);
        const errorResponse = {
          role: 'assistant',
          content: `Sorry, I encountered an error while processing your question: ${error.message}`,
          timestamp: new Date()
        };
        setMessages(prev => ({
          ...prev,
          [currentAssistant.id]: [...(prev[currentAssistant.id] || []), errorResponse]
        }));
      }
      setIsProcessing(false);
      return;
    }

    // Check for workflow triggers
    const triggeredWorkflows = [];
    if (queryLower.includes('search everywhere') || queryLower.includes('find all')) {
      triggeredWorkflows.push('Multi-Source Deep Search');
    }
    if (queryLower.includes('create document') || queryLower.includes('generate report')) {
      triggeredWorkflows.push('Document Generator');
    }
    if (queryLower.includes('summarize')) {
      triggeredWorkflows.push('Smart Document Summarization');
    }

    // Get connected data source names for this assistant
    const assistantDataSources = dataSources
      .filter(ds => currentAssistant.dataSources.includes(ds.id) && ds.status === 'connected')
      .map(ds => ds.name);

    // Simulate AI response
    setTimeout(() => {
      let responseContent = `I found relevant information across your configured sources. Based on your query "${inputValue}", here's what I discovered:\n\n• Found 12 relevant documents\n• 5 related conversations\n• 3 pages with matching content\n\nWould you like me to provide more details from any specific source?`;
      
      // Add workflow notifications
      if (triggeredWorkflows.length > 0) {
        responseContent = `⚡ Workflows triggered: ${triggeredWorkflows.join(', ')}\n\n` + responseContent;
      }

      const aiResponse = {
        role: 'assistant',
        content: responseContent,
        timestamp: new Date(),
        sources: assistantDataSources.slice(0, 3),
        workflows: triggeredWorkflows
      };
      setMessages(prev => ({
        ...prev,
        [currentAssistant.id]: [...(prev[currentAssistant.id] || []), aiResponse]
      }));
      setIsProcessing(false);

      // Update workflow run counts
      triggeredWorkflows.forEach(workflowName => {
        setWorkflows(prev => prev.map(w => 
          w.name === workflowName ? { ...w, runs: w.runs + 1 } : w
        ));
      });

      // Add to query history
      setQueryHistory(prev => [{
        id: Date.now(),
        user: 'Sarah Johnson',
        query: inputValue,
        timestamp: 'Just now',
        assistant: currentAssistant.name,
        sources: assistantDataSources.slice(0, 2),
        tokens: Math.floor(Math.random() * 1000) + 200
      }, ...prev]);

      // Add suggested follow-ups after 1 second
      setTimeout(() => {
        const followUpMessage = {
          role: 'system',
          content: 'Suggested follow-ups:',
          timestamp: new Date(),
          followUps: [
            'Show me more details from Google Drive',
            'Summarize the key findings',
            'Create a report from this information'
          ]
        };
        setMessages(prev => ({
          ...prev,
          [currentAssistant.id]: [...(prev[currentAssistant.id] || []), followUpMessage]
        }));
      }, 1000);
    }, 1500);
  };

  const handleVoiceToggle = () => {
    setIsRecording(!isRecording);
    if (!isRecording) {
      // Simulate voice recording
      setTimeout(() => {
        setInputValue('What are the key findings from the latest market research?');
        setIsRecording(false);
      }, 3000);
    }
  };

  const handleFileUpload = () => {
    fileInputRef.current?.click();
  };

  // Sidebar Component
  const Sidebar = () => (
    <div className={`${sidebarOpen ? 'w-64' : 'w-16'} bg-gradient-to-b from-blue-900 to-blue-800 text-white transition-all duration-300 flex flex-col`}>
      <div className="p-4 flex items-center justify-between border-b border-blue-700">
        {sidebarOpen && <h1 className="text-xl font-bold">KnowledgeAI</h1>}
        <button onClick={() => setSidebarOpen(!sidebarOpen)} className="p-2 hover:bg-blue-700 rounded-lg">
          <Menu className="w-5 h-5" />
        </button>
      </div>

      <nav className="flex-1 p-3 space-y-1">
        <NavItem icon={<MessageSquare className="w-5 h-5" />} label="Chat" active={currentView === 'chat'} onClick={() => setCurrentView('chat')} />
        <NavItem icon={<Users className="w-5 h-5" />} label="Assistants" active={currentView === 'assistants'} onClick={() => setCurrentView('assistants')} />
        <NavItem icon={<Database className="w-5 h-5" />} label="Data Sources" active={currentView === 'sources'} onClick={() => setCurrentView('sources')} />
        <NavItem icon={<Zap className="w-5 h-5" />} label="Workflows" active={currentView === 'workflows'} onClick={() => setCurrentView('workflows')} />
        <NavItem icon={<Users className="w-5 h-5" />} label="Team Members" active={currentView === 'users'} onClick={() => setCurrentView('users')} />
        <NavItem icon={<Shield className="w-5 h-5" />} label="Access Management" active={currentView === 'access'} onClick={() => setCurrentView('access')} />
        <NavItem icon={<Clock className="w-5 h-5" />} label="Query History" active={currentView === 'history'} onClick={() => setCurrentView('history')} />
        <NavItem icon={<CreditCard className="w-5 h-5" />} label="Billing" active={currentView === 'billing'} onClick={() => setCurrentView('billing')} />
        <NavItem icon={<Settings className="w-5 h-5" />} label="Settings" active={currentView === 'settings'} onClick={() => setCurrentView('settings')} />
      </nav>

      <div className="p-3 border-t border-blue-700">
        {currentUser && sidebarOpen && (
          <div className="bg-blue-800 rounded-lg p-3 mb-3">
            <div className="text-xs text-blue-300 mb-1">Logged in as</div>
            <div className="font-semibold">{currentUser.username}</div>
            <div className="text-xs text-blue-300 mt-2">{currentUser.role} role</div>
          </div>
        )}
        {currentUser ? (
          <NavItem icon={<LogOut className="w-5 h-5" />} label="Logout" onClick={logout} />
        ) : (
          <NavItem icon={<Users className="w-5 h-5" />} label="Logout" onClick={() => setCurrentView('sources')} />
        )}
      </div>
    </div>
  );

  const NavItem = ({ icon, label, active, onClick }) => (
    <button
      onClick={onClick}
      className={`w-full flex items-center space-x-3 px-3 py-2.5 rounded-lg transition-all ${
        active ? 'bg-blue-700 text-white' : 'text-blue-100 hover:bg-blue-700/50'
      }`}
    >
      {icon}
      {sidebarOpen && <span className="text-sm font-medium">{label}</span>}
    </button>
  );

  // Chat View
  const ChatView = () => {
    const currentMessages = messages[currentAssistant.id] || [];
    const activeAssistantDataSources = dataSources.filter(ds => 
      currentAssistant.dataSources.includes(ds.id) && ds.status === 'connected'
    );

    return (
      <div className="flex-1 flex flex-col bg-white">
        {/* Header */}
        <div className="bg-white border-b border-gray-200 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              {/* Assistant Switcher */}
              <div className="relative">
                <select
                  value={currentAssistant.id}
                  onChange={(e) => {
                    const assistant = assistants.find(a => a.id === parseInt(e.target.value));
                    setCurrentAssistant(assistant);
                  }}
                  className="appearance-none bg-gradient-to-r from-blue-50 to-purple-50 border-2 border-blue-200 rounded-xl px-4 py-3 pr-10 font-semibold text-gray-900 cursor-pointer hover:border-blue-400 transition-all focus:outline-none focus:border-blue-500"
                >
                  {assistants.filter(a => a.isActive).map(assistant => (
                    <option key={assistant.id} value={assistant.id}>
                      {assistant.icon} {assistant.name}
                    </option>
                  ))}
                </select>
                <ChevronDown className="w-5 h-5 text-gray-600 absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none" />
              </div>
              <div>
                <p className="text-sm text-gray-600">{currentAssistant.description}</p>
                {currentUser ? (
                  <p className="text-xs text-gray-500 mt-1">
                    ACL: {userDataSources.filter(ds => ds.permissions.includes('chat')).length} accessible sources • Role: {currentUser.role}
                  </p>
                ) : (
                  <p className="text-xs text-red-500 mt-1">
                                      </p>
                )}
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <button 
                onClick={() => setCurrentView('assistants')}
                className="px-4 py-2 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-all text-sm font-medium"
              >
                Manage Assistants
              </button>
              <button className="p-2 hover:bg-gray-100 rounded-lg" title="Notifications">
                <Bell className="w-5 h-5 text-gray-600" />
              </button>
              <button className="p-2 hover:bg-gray-100 rounded-lg" title="Help">
                <HelpCircle className="w-5 h-5 text-gray-600" />
              </button>
            </div>
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-6 py-6 bg-gray-50">
          <div className="max-w-4xl mx-auto space-y-6">
            {currentMessages.map((msg, idx) => (
              <div key={idx}>
                {/* Regular messages */}
                {(msg.role === 'user' || msg.role === 'assistant') && (
                  <div className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-3xl ${msg.role === 'user' ? 'bg-blue-600 text-white' : 'bg-white border border-gray-200'} rounded-2xl px-6 py-4 shadow-sm`}>
                      {msg.role === 'assistant' && (
                        <div className="flex items-center space-x-2 mb-2">
                          <div className={`w-8 h-8 bg-gradient-to-br from-${currentAssistant.color}-500 to-${currentAssistant.color}-600 rounded-full flex items-center justify-center text-white text-lg`}>
                            {currentAssistant.icon}
                          </div>
                          <span className="font-semibold text-gray-900">{currentAssistant.name}</span>
                        </div>
                      )}
                      
                      {/* Workflow badges */}
                      {msg.workflows && msg.workflows.length > 0 && (
                        <div className="mb-3 pb-3 border-b border-gray-200">
                          <div className="flex flex-wrap gap-2">
                            {msg.workflows.map((workflow, i) => (
                              <span key={i} className="inline-flex items-center text-xs bg-purple-50 text-purple-700 px-2 py-1 rounded-full font-medium">
                                <Zap className="w-3 h-3 mr-1" />
                                {workflow}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      <div className={`${msg.role === 'user' ? 'text-white' : 'text-gray-800'} whitespace-pre-wrap`}>
                        {msg.content}
                      </div>
                      {msg.citations && msg.citations.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-gray-200 flex flex-col gap-2">
                          <p className="text-xs text-gray-500 font-semibold">Citations:</p>
                          {msg.citations.map((citation, i) => (
                            <div key={i} className="bg-blue-50 text-blue-700 px-3 py-2 rounded-lg text-sm">
                              <p className="font-medium">📎 {citation.source.split(/[\\/]/).pop()}</p>
                              <p className="text-xs text-blue-600 mt-1 line-clamp-2">"...{citation.content.substring(0, 150)}..."</p>
                            </div>
                          ))}
                        </div>
                      )}
                      <div className={`text-xs mt-2 ${msg.role === 'user' ? 'text-blue-100' : 'text-gray-400'}`}>
                        {msg.timestamp.toLocaleTimeString()}
                      </div>
                    </div>
                  </div>
                )}

                {/* Follow-up suggestions */}
                {msg.role === 'system' && msg.followUps && (
                  <div className="flex justify-center">
                    <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-xl px-6 py-4 border border-purple-200 max-w-3xl">
                      <div className="flex items-center space-x-2 mb-3">
                        <MessageSquare className="w-4 h-4 text-purple-600" />
                        <span className="text-sm font-semibold text-purple-900">{msg.content}</span>
                      </div>
                      <div className="space-y-2">
                        {msg.followUps.map((followUp, i) => (
                          <button
                            key={i}
                            onClick={() => {
                              setInputValue(followUp);
                              setTimeout(() => handleSendMessage(), 100);
                            }}
                            className="w-full text-left px-4 py-2 bg-white hover:bg-purple-50 rounded-lg text-sm text-gray-700 hover:text-purple-700 transition-all border border-gray-200 hover:border-purple-300"
                          >
                            {followUp}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
            {isProcessing && (
              <div className="flex justify-start">
                <div className="bg-white border border-gray-200 rounded-2xl px-6 py-4 shadow-sm">
                  <div className="flex items-center space-x-3">
                    <div className="flex space-x-1">
                      <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce"></div>
                      <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                      <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                    </div>
                    <span className="text-sm text-gray-600">Searching across your data sources...</span>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
        </div>

        {/* Input */}
        <div className="bg-white border-t border-gray-200 px-6 py-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center space-x-3 bg-gray-50 rounded-2xl border-2 border-gray-200 focus-within:border-blue-500 transition-all px-4 py-3">
              <button onClick={handleFileUpload} className="text-gray-400 hover:text-gray-600 transition-colors">
                <Upload className="w-5 h-5" />
              </button>
              <input
                ref={chatInputRef}
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                onFocus={() => setIsInputFocused(true)}
                onBlur={() => setIsInputFocused(false)}
                placeholder={`Ask ${currentAssistant.name}...`}
                className="flex-1 bg-transparent outline-none text-gray-900 placeholder-gray-400"
              />
              <button
                onClick={handleVoiceToggle}
                className={`transition-all ${isRecording ? 'text-red-600 animate-pulse' : 'text-gray-400 hover:text-gray-600'}`}
              >
                {isRecording ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
              </button>
              <button
                onClick={handleSendMessage}
                disabled={!inputValue.trim() || isProcessing}
                className="bg-blue-600 text-white p-2 rounded-xl hover:bg-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Send className="w-5 h-5" />
              </button>
            </div>
            <p className="text-xs text-gray-400 mt-2 text-center">
              {isRecording ? 'Listening... Speak now' : 'Press the microphone to use voice input'}
            </p>
          </div>
        </div>

        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          onChange={(e) => {
            if (e.target.files?.[0]) {
              setMessages(prev => ({
                ...prev,
                [currentAssistant.id]: [...(prev[currentAssistant.id] || []), {
                  role: 'user',
                  content: `📎 Uploaded: ${e.target.files[0].name}`,
                  timestamp: new Date()
                }]
              }));
            }
          }}
        />
      </div>
    );
  };

  // Data Sources View
  const DataSourcesView = () => {
    const [activeTab, setActiveTab] = useState('connection');

    const handleConnect = (sourceId) => {
      // Simulate OAuth flow
      alert('Opening authentication window...');
      setTimeout(() => {
        setDataSources(prev => prev.map(ds =>
          ds.id === sourceId ? { ...ds, status: 'connected', lastSync: 'Just now', files: Math.floor(Math.random() * 1000) + 100 } : ds
        ));
        alert('Successfully connected!');
      }, 1500);
    };

    const handleSyncNow = (sourceId) => {
      setDataSources(prev => prev.map(ds =>
        ds.id === sourceId ? { ...ds, status: 'syncing', lastSync: 'Syncing...' } : ds
      ));

      setTimeout(() => {
        const newFiles = Math.floor(Math.random() * 50) + 5;
        setDataSources(prev => prev.map(ds =>
          ds.id === sourceId ? {
            ...ds,
            status: 'connected',
            lastSync: 'Just now',
            files: ds.files + newFiles
          } : ds
        ));

        const source = dataSources.find(ds => ds.id === sourceId);
        setSyncLogs(prev => [{
          id: Date.now(),
          source: source.name,
          action: 'Sync completed',
          status: 'success',
          files: newFiles,
          timestamp: 'Just now',
          details: `Added ${newFiles} new items`
        }, ...prev]);
      }, 3000);
    };

    return (
      <div className="flex-1 overflow-y-auto bg-gray-50 p-6">
        <div className="max-w-6xl mx-auto">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Data Sources</h2>
              <p className="text-gray-600 mt-1">Connect and manage your knowledge sources</p>
            </div>
            <button
              onClick={() => setConfiguringSource({
                id: Date.now(),
                name: '',
                type: 'custom',
                status: 'disconnected',
                icon: '🔌',
                files: 0,
                lastSync: 'Never',
                config: { syncFrequency: 'hourly', fileTypes: [] },
                isNew: true
              })}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all flex items-center space-x-2"
            >
              <Plus className="w-4 h-4" />
              <span>Add Source</span>
            </button>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-4 gap-4 mb-6">
            <div className="bg-white rounded-xl p-4 border border-gray-200">
              <div className="text-sm text-gray-600 mb-1">Total Sources</div>
              <div className="text-2xl font-bold text-gray-900">{dataSources.length}</div>
            </div>
            <div className="bg-white rounded-xl p-4 border border-gray-200">
              <div className="text-sm text-gray-600 mb-1">Connected</div>
              <div className="text-2xl font-bold text-green-600">{dataSources.filter(d => d.status === 'connected').length}</div>
            </div>
            <div className="bg-white rounded-xl p-4 border border-gray-200">
              <div className="text-sm text-gray-600 mb-1">Total Files</div>
              <div className="text-2xl font-bold text-gray-900">{dataSources.reduce((acc, d) => acc + d.files, 0).toLocaleString()}</div>
            </div>
            <div className="bg-white rounded-xl p-4 border border-gray-200">
              <div className="text-sm text-gray-600 mb-1">Storage Used</div>
              <div className="text-2xl font-bold text-gray-900">156 GB</div>
            </div>
          </div>

          {/* Data Sources List */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden mb-6">
            <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
              <h3 className="font-semibold text-gray-900">Connected Sources</h3>
            </div>
            <div className="divide-y divide-gray-200">
              {dataSources.map(source => (
                <div key={source.id} className="px-6 py-4 hover:bg-gray-50 transition-colors">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className="text-3xl">{source.icon}</div>
                      <div>
                        <h4 className="font-semibold text-gray-900">{source.name}</h4>
                        <p className="text-sm text-gray-500">{source.files.toLocaleString()} files • Last sync: {source.lastSync}</p>
                        {source.config && source.status === 'connected' && (
                          <p className="text-xs text-gray-400 mt-1">
                            Sync: {source.config.syncFrequency} •
                            {source.config.folders && ` ${source.config.folders.length} folders`}
                            {source.config.channels && ` ${source.config.channels.length} channels`}
                            {source.config.spaces && ` ${source.config.spaces.length} spaces`}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                        source.status === 'connected' ? 'bg-green-100 text-green-700' :
                        source.status === 'syncing' ? 'bg-blue-100 text-blue-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {source.status === 'connected' ? '✓ Connected' :
                         source.status === 'syncing' ? '⟳ Syncing' :
                         '○ Disconnected'}
                      </span>
                      <button
                        onClick={() => setConfiguringSource(source)}
                        className="p-2 hover:bg-gray-100 rounded-lg"
                        title="Configure"
                      >
                        <Settings className="w-5 h-5 text-gray-600" />
                      </button>
                      {source.status === 'connected' && (
                        <button
                          onClick={() => handleSyncNow(source.id)}
                          className="px-3 py-1 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 text-sm font-medium"
                        >
                          Sync Now
                        </button>
                      )}
                      {source.status === 'disconnected' && (
                        <button
                          onClick={() => handleConnect(source.id)}
                          className="px-3 py-1 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
                        >
                          Connect
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Sync Logs */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden mb-6">
            <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
              <h3 className="font-semibold text-gray-900">Recent Sync Activity</h3>
            </div>
            <div className="divide-y divide-gray-200">
              {syncLogs.slice(0, 5).map(log => (
                <div key={log.id} className="px-6 py-4 hover:bg-gray-50 transition-colors">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        log.status === 'success' ? 'bg-green-100' : 'bg-red-100'
                      }`}>
                        {log.status === 'success' ? (
                          <Check className="w-5 h-5 text-green-600" />
                        ) : (
                          <AlertCircle className="w-5 h-5 text-red-600" />
                        )}
                      </div>
                      <div>
                        <div className="flex items-center space-x-2">
                          <span className="font-medium text-gray-900">{log.source}</span>
                          <span className="text-xs text-gray-400">•</span>
                          <span className="text-sm text-gray-600">{log.action}</span>
                        </div>
                        <p className="text-sm text-gray-500">{log.details}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm text-gray-600">{log.files} files</div>
                      <div className="text-xs text-gray-400">{log.timestamp}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Available Integrations */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h3 className="font-semibold text-gray-900 mb-4">Available Integrations</h3>
            <div className="grid grid-cols-4 gap-4">
              {['Dropbox', 'OneDrive', 'Box', 'Jira', 'GitHub', 'GitLab', 'Trello', 'Asana'].map(integration => (
                <button
                  key={integration}
                  onClick={() => setConfiguringSource({
                    id: Date.now(),
                    name: integration,
                    type: integration.toLowerCase(),
                    status: 'disconnected',
                    icon: '🔌',
                    files: 0,
                    lastSync: 'Never',
                    config: { syncFrequency: 'hourly', fileTypes: [] },
                    isNew: true
                  })}
                  className="p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-all text-center"
                >
                  <div className="text-2xl mb-2">+</div>
                  <div className="text-sm font-medium text-gray-700">{integration}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Configuration Modal */}
          {configuringSource && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-6">
              <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                {/* Modal Header */}
                <div className="sticky top-0 bg-white border-b border-gray-200 px-8 py-6 z-10">
                  <div className="flex justify-between items-center">
                    <div className="flex items-center space-x-4">
                      <div className="text-4xl">{configuringSource.icon}</div>
                      <div>
                        <h3 className="text-2xl font-bold text-gray-900">{configuringSource.name}</h3>
                        <p className="text-sm text-gray-600">
                          {configuringSource.status === 'connected' ? 'Configure your connection' : 'Set up your data source'}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => setConfiguringSource(null)}
                      className="p-2 hover:bg-gray-100 rounded-lg"
                    >
                      <X className="w-6 h-6" />
                    </button>
                  </div>

                  {/* Tabs */}
                  <div className="flex space-x-1 mt-4 border-b border-gray-200">
                    {['connection', 'folders', 'sync', 'filters', 'logs'].map(tab => (
                      <button
                        key={tab}
                        onClick={() => setActiveTab(tab)}
                        className={`px-4 py-2 font-medium text-sm capitalize transition-all ${
                          activeTab === tab
                            ? 'text-blue-600 border-b-2 border-blue-600'
                            : 'text-gray-600 hover:text-gray-900'
                        }`}
                      >
                        {tab}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Modal Content */}
                <div className="px-8 py-6">
                  {/* Connection Tab */}
                  {activeTab === 'connection' && (
                    <div className="space-y-6">
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <h4 className="font-semibold text-blue-900 mb-2">Connection Status</h4>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <div className={`w-3 h-3 rounded-full ${
                              configuringSource.status === 'connected' ? 'bg-green-500' : 'bg-gray-400'
                            }`}></div>
                            <span className="text-sm text-gray-700">
                              {configuringSource.status === 'connected' ? 'Connected and syncing' : 'Not connected'}
                            </span>
                          </div>
                          {configuringSource.status === 'connected' ? (
                            <button
                              onClick={() => {
                                setDataSources(prev => prev.map(ds =>
                                  ds.id === configuringSource.id ? { ...ds, status: 'disconnected' } : ds
                                ));
                                setConfiguringSource({ ...configuringSource, status: 'disconnected' });
                              }}
                              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm font-medium"
                            >
                              Disconnect
                            </button>
                          ) : (
                            <button
                              onClick={() => handleConnect(configuringSource.id)}
                              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
                            >
                              Connect with OAuth
                            </button>
                          )}
                        </div>
                      </div>

                      {configuringSource.status === 'connected' && (
                        <>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">API Credentials</label>
                            <div className="bg-gray-50 border border-gray-300 rounded-lg p-4">
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-sm text-gray-600">Access Token</span>
                                <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">Valid</span>
                              </div>
                              <code className="text-xs text-gray-700 font-mono">•••••••••••••••••••••••••••</code>
                              <div className="mt-3 text-xs text-gray-500">
                                Token expires: Dec 31, 2025
                              </div>
                            </div>
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Connection Health</label>
                            <div className="grid grid-cols-3 gap-4">
                              <div className="bg-gray-50 rounded-lg p-4">
                                <div className="text-2xl font-bold text-green-600">98.5%</div>
                                <div className="text-xs text-gray-600 mt-1">Uptime</div>
                              </div>
                              <div className="bg-gray-50 rounded-lg p-4">
                                <div className="text-2xl font-bold text-blue-600">1.2s</div>
                                <div className="text-xs text-gray-600 mt-1">Avg Response</div>
                              </div>
                              <div className="bg-gray-50 rounded-lg p-4">
                                <div className="text-2xl font-bold text-purple-600">145</div>
                                <div className="text-xs text-gray-600 mt-1">API Calls/day</div>
                              </div>
                            </div>
                          </div>

                          <button className="w-full px-4 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-all font-medium">
                            Test Connection
                          </button>
                        </>
                      )}
                    </div>
                  )}

                  {/* Folders/Channels Tab */}
                  {activeTab === 'folders' && (
                    <div className="space-y-6">
                      <div>
                        <div className="flex justify-between items-center mb-4">
                          <div>
                            <h4 className="font-semibold text-gray-900">
                              Select {configuringSource.type === 'slack' ? 'Channels' : configuringSource.type === 'confluence' ? 'Spaces' : 'Folders'}
                            </h4>
                            <p className="text-sm text-gray-600">Choose which content to sync</p>
                          </div>
                          <button className="px-3 py-1.5 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 text-sm font-medium">
                            Select All
                          </button>
                        </div>

                        <div className="border border-gray-300 rounded-lg divide-y divide-gray-200 max-h-96 overflow-y-auto">
                          {configuringSource.config?.folders?.map((folder, idx) => (
                            <label key={idx} className="flex items-center justify-between p-4 hover:bg-gray-50 cursor-pointer">
                              <div className="flex items-center space-x-3">
                                <input type="checkbox" defaultChecked className="w-5 h-5 text-blue-600 rounded" />
                                <Folder className="w-5 h-5 text-gray-400" />
                                <span className="text-gray-900">{folder}</span>
                              </div>
                              <span className="text-sm text-gray-500">{Math.floor(Math.random() * 200) + 10} files</span>
                            </label>
                          ))}
                          {configuringSource.config?.channels?.map((channel, idx) => (
                            <label key={idx} className="flex items-center justify-between p-4 hover:bg-gray-50 cursor-pointer">
                              <div className="flex items-center space-x-3">
                                <input type="checkbox" defaultChecked className="w-5 h-5 text-blue-600 rounded" />
                                <span className="text-gray-900">#{channel}</span>
                              </div>
                              <span className="text-sm text-gray-500">{Math.floor(Math.random() * 5000) + 100} messages</span>
                            </label>
                          ))}
                          {configuringSource.config?.spaces?.map((space, idx) => (
                            <label key={idx} className="flex items-center justify-between p-4 hover:bg-gray-50 cursor-pointer">
                              <div className="flex items-center space-x-3">
                                <input type="checkbox" defaultChecked className="w-5 h-5 text-blue-600 rounded" />
                                <Globe className="w-5 h-5 text-gray-400" />
                                <span className="text-gray-900">{space}</span>
                              </div>
                              <span className="text-sm text-gray-500">{Math.floor(Math.random() * 150) + 20} pages</span>
                            </label>
                          ))}
                          {/* Add more option */}
                          <button className="w-full p-4 text-center text-blue-600 hover:bg-blue-50 font-medium text-sm">
                            + Add More
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Sync Settings Tab */}
                  {activeTab === 'sync' && (
                    <div className="space-y-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-3">Sync Frequency</label>
                        <div className="grid grid-cols-4 gap-3">
                          {['realtime', 'hourly', 'daily', 'weekly'].map(freq => (
                            <button
                              key={freq}
                              onClick={() => {
                                const updated = { ...configuringSource, config: { ...configuringSource.config, syncFrequency: freq }};
                                setConfiguringSource(updated);
                              }}
                              className={`px-4 py-3 border-2 rounded-lg capitalize font-medium transition-all ${
                                configuringSource.config?.syncFrequency === freq
                                  ? 'border-blue-500 bg-blue-50 text-blue-700'
                                  : 'border-gray-300 text-gray-700 hover:border-gray-400'
                              }`}
                            >
                              {freq}
                            </button>
                          ))}
                        </div>
                      </div>

                      <div className="border-t border-gray-200 pt-6">
                        <h4 className="font-semibold text-gray-900 mb-4">Advanced Options</h4>
                        <div className="space-y-4">
                          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                            <div>
                              <div className="font-medium text-gray-900">Auto-sync new content</div>
                              <div className="text-sm text-gray-500">Automatically sync when new files are added</div>
                            </div>
                            <button className="relative inline-flex h-6 w-11 items-center rounded-full bg-blue-600">
                              <span className="inline-block h-4 w-4 transform rounded-full bg-white transition translate-x-6"></span>
                            </button>
                          </div>

                          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                            <div>
                              <div className="font-medium text-gray-900">Include metadata</div>
                              <div className="text-sm text-gray-500">Sync file metadata and properties</div>
                            </div>
                            <button className="relative inline-flex h-6 w-11 items-center rounded-full bg-blue-600">
                              <span className="inline-block h-4 w-4 transform rounded-full bg-white transition translate-x-6"></span>
                            </button>
                          </div>

                          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                            <div>
                              <div className="font-medium text-gray-900">Sync deleted items</div>
                              <div className="text-sm text-gray-500">Remove deleted files from index</div>
                            </div>
                            <button className="relative inline-flex h-6 w-11 items-center rounded-full bg-gray-300">
                              <span className="inline-block h-4 w-4 transform rounded-full bg-white transition translate-x-1"></span>
                            </button>
                          </div>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Storage Limit (per source)</label>
                        <select className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500">
                          <option>10 GB</option>
                          <option>50 GB</option>
                          <option>100 GB</option>
                          <option>Unlimited</option>
                        </select>
                      </div>
                    </div>
                  )}

                  {/* Filters Tab */}
                  {activeTab === 'filters' && (
                    <div className="space-y-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-3">File Types</label>
                        <div className="grid grid-cols-3 gap-3">
                          {['pdf', 'docx', 'xlsx', 'pptx', 'txt', 'md', 'csv', 'png', 'jpg'].map(type => (
                            <label key={type} className="flex items-center space-x-2 p-3 border border-gray-300 rounded-lg hover:bg-gray-50 cursor-pointer">
                              <input
                                type="checkbox"
                                defaultChecked={configuringSource.config?.fileTypes?.includes(type)}
                                className="w-4 h-4 text-blue-600 rounded"
                              />
                              <span className="text-sm font-medium text-gray-700 uppercase">{type}</span>
                            </label>
                          ))}
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Exclude Patterns</label>
                        <textarea
                          placeholder="Enter patterns to exclude, one per line&#10;e.g., *.tmp&#10;temp/*&#10;*backup*"
                          rows={4}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 font-mono text-sm"
                        />
                        <p className="text-xs text-gray-500 mt-2">Files matching these patterns will not be synced</p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Minimum File Size</label>
                        <div className="flex items-center space-x-3">
                          <input
                            type="number"
                            defaultValue="0"
                            className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
                          />
                          <select className="px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500">
                            <option>KB</option>
                            <option>MB</option>
                          </select>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Maximum File Size</label>
                        <div className="flex items-center space-x-3">
                          <input
                            type="number"
                            defaultValue="100"
                            className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
                          />
                          <select className="px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500">
                            <option>MB</option>
                            <option>GB</option>
                          </select>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Logs Tab */}
                  {activeTab === 'logs' && (
                    <div className="space-y-4">
                      <div className="flex justify-between items-center">
                        <h4 className="font-semibold text-gray-900">Sync History</h4>
                        <button className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm font-medium">
                          Export Logs
                        </button>
                      </div>

                      <div className="border border-gray-300 rounded-lg divide-y divide-gray-200 max-h-96 overflow-y-auto">
                        {syncLogs
                          .filter(log => log.source === configuringSource.name)
                          .map(log => (
                            <div key={log.id} className="p-4">
                              <div className="flex items-start space-x-3">
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                                  log.status === 'success' ? 'bg-green-100' : 'bg-red-100'
                                }`}>
                                  {log.status === 'success' ? (
                                    <Check className="w-4 h-4 text-green-600" />
                                  ) : (
                                    <AlertCircle className="w-4 h-4 text-red-600" />
                                  )}
                                </div>
                                <div className="flex-1">
                                  <div className="flex items-center justify-between mb-1">
                                    <span className="font-medium text-gray-900">{log.action}</span>
                                    <span className="text-xs text-gray-500">{log.timestamp}</span>
                                  </div>
                                  <p className="text-sm text-gray-600">{log.details}</p>
                                  <div className="mt-2 flex items-center space-x-4 text-xs text-gray-500">
                                    <span>{log.files} files processed</span>
                                    <span>•</span>
                                    <span>Status: {log.status}</span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        {syncLogs.filter(log => log.source === configuringSource.name).length === 0 && (
                          <div className="p-8 text-center text-gray-500">
                            <Activity className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                            <p>No sync history yet</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Modal Footer */}
                <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-8 py-4 flex justify-between items-center">
                  <button
                    onClick={() => {
                      if (window.confirm(`Remove ${configuringSource.name} from data sources?`)) {
                        setDataSources(prev => prev.filter(ds => ds.id !== configuringSource.id));
                        setConfiguringSource(null);
                      }
                    }}
                    className="px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg font-medium"
                  >
                    Remove Source
                  </button>
                  <div className="flex space-x-3">
                    <button
                      onClick={() => setConfiguringSource(null)}
                      className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => {
                        setDataSources(prev =>
                          prev.some(ds => ds.id === configuringSource.id)
                            ? prev.map(ds => ds.id === configuringSource.id ? configuringSource : ds)
                            : [...prev, configuringSource]
                        );
                        setConfiguringSource(null);
                      }}
                      className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
                    >
                      Save Changes
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };



  // Workflows View
  const WorkflowsView = () => {
    const categories = ['All', 'Chat Enhancement', 'Document Processing', 'Integration', 'Performance', 'Analytics', 'Productivity', 'Notifications'];
    const [selectedCategory, setSelectedCategory] = useState('All');

    const filteredWorkflows = selectedCategory === 'All' 
      ? workflows 
      : workflows.filter(w => w.category === selectedCategory);

    return (
      <div className="flex-1 overflow-y-auto bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Automation Workflows</h2>
              <p className="text-gray-600 mt-1">Enhance chat capabilities with intelligent workflows</p>
            </div>
            <button 
              onClick={() => setEditingWorkflow({
                id: Date.now(),
                name: '',
                trigger: '',
                action: '',
                status: 'active',
                runs: 0,
                description: '',
                category: 'Chat Enhancement',
                avgTime: '0s',
                successRate: 0,
                isNew: true
              })}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all flex items-center space-x-2"
            >
              <Plus className="w-4 h-4" />
              <span>Create Workflow</span>
            </button>
          </div>

          {/* Workflow Stats */}
          <div className="grid grid-cols-4 gap-4 mb-6">
            <div className="bg-white rounded-xl p-4 border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm text-gray-600 mb-1">Active Workflows</div>
                  <div className="text-2xl font-bold text-gray-900">{workflows.filter(w => w.status === 'active').length}</div>
                </div>
                <Zap className="w-8 h-8 text-blue-600" />
              </div>
            </div>
            <div className="bg-white rounded-xl p-4 border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm text-gray-600 mb-1">Total Executions</div>
                  <div className="text-2xl font-bold text-gray-900">{workflows.reduce((acc, w) => acc + w.runs, 0).toLocaleString()}</div>
                </div>
                <Activity className="w-8 h-8 text-green-600" />
              </div>
            </div>
            <div className="bg-white rounded-xl p-4 border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm text-gray-600 mb-1">Avg Success Rate</div>
                  <div className="text-2xl font-bold text-gray-900">
                    {(workflows.reduce((acc, w) => acc + w.successRate, 0) / workflows.length).toFixed(1)}%
                  </div>
                </div>
                <TrendingUp className="w-8 h-8 text-purple-600" />
              </div>
            </div>
            <div className="bg-white rounded-xl p-4 border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm text-gray-600 mb-1">Time Saved</div>
                  <div className="text-2xl font-bold text-gray-900">124h</div>
                </div>
                <Clock className="w-8 h-8 text-orange-600" />
              </div>
            </div>
          </div>

          {/* Category Filter */}
          <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6">
            <div className="flex items-center space-x-2 overflow-x-auto">
              {categories.map(category => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-4 py-2 rounded-lg font-medium text-sm whitespace-nowrap transition-all ${
                    selectedCategory === category
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {category}
                  {category !== 'All' && (
                    <span className="ml-2 text-xs opacity-75">
                      ({workflows.filter(w => w.category === category).length})
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Workflows Grid */}
          <div className="grid grid-cols-1 gap-4 mb-6">
            {filteredWorkflows.map(workflow => (
              <div key={workflow.id} className="bg-white rounded-xl border border-gray-200 p-6 hover:border-blue-300 transition-all">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-start space-x-4 flex-1">
                    <div className={`w-12 h-12 bg-gradient-to-br ${
                      workflow.category === 'Chat Enhancement' ? 'from-blue-500 to-blue-600' :
                      workflow.category === 'Document Processing' ? 'from-green-500 to-green-600' :
                      workflow.category === 'Integration' ? 'from-purple-500 to-purple-600' :
                      workflow.category === 'Performance' ? 'from-orange-500 to-orange-600' :
                      workflow.category === 'Analytics' ? 'from-pink-500 to-pink-600' :
                      workflow.category === 'Productivity' ? 'from-indigo-500 to-indigo-600' :
                      'from-gray-500 to-gray-600'
                    } rounded-lg flex items-center justify-center flex-shrink-0`}>
                      <Zap className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-3 mb-2">
                        <h3 className="font-semibold text-gray-900 text-lg">{workflow.name}</h3>
                        <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs font-medium">
                          {workflow.category}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mb-3">{workflow.description}</p>
                      
                      {/* Metrics */}
                      <div className="grid grid-cols-4 gap-4 mb-4">
                        <div className="bg-gray-50 rounded-lg p-3">
                          <div className="text-xs text-gray-500 mb-1">Executions</div>
                          <div className="font-semibold text-gray-900">{workflow.runs.toLocaleString()}</div>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-3">
                          <div className="text-xs text-gray-500 mb-1">Avg Time</div>
                          <div className="font-semibold text-gray-900">{workflow.avgTime}</div>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-3">
                          <div className="text-xs text-gray-500 mb-1">Success Rate</div>
                          <div className="font-semibold text-green-600">{workflow.successRate}%</div>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-3">
                          <div className="text-xs text-gray-500 mb-1">Status</div>
                          <div className={`font-semibold ${workflow.status === 'active' ? 'text-green-600' : 'text-gray-600'}`}>
                            {workflow.status === 'active' ? '● Active' : '○ Paused'}
                          </div>
                        </div>
                      </div>

                      {/* Workflow Flow */}
                      <div className="bg-gradient-to-r from-gray-50 to-blue-50 rounded-lg p-4">
                        <div className="flex items-center space-x-3">
                          <div className="flex-1">
                            <div className="text-xs font-medium text-gray-500 mb-1">TRIGGER</div>
                            <div className="bg-white border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-900">
                              {workflow.trigger}
                            </div>
                          </div>
                          <div className="text-gray-400 flex-shrink-0">→</div>
                          <div className="flex-1">
                            <div className="text-xs font-medium text-gray-500 mb-1">ACTION</div>
                            <div className="bg-white border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-900">
                              {workflow.action}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2 ml-4">
                    <button 
                      onClick={() => {
                        setWorkflows(prev => prev.map(w => 
                          w.id === workflow.id 
                            ? { ...w, status: w.status === 'active' ? 'paused' : 'active' }
                            : w
                        ));
                      }}
                      className={`p-2 rounded-lg transition-colors ${
                        workflow.status === 'active' 
                          ? 'bg-green-50 text-green-600 hover:bg-green-100' 
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                      title={workflow.status === 'active' ? 'Pause' : 'Activate'}
                    >
                      {workflow.status === 'active' ? (
                        <Activity className="w-5 h-5" />
                      ) : (
                        <Activity className="w-5 h-5" />
                      )}
                    </button>
                    <button 
                      onClick={() => setEditingWorkflow(workflow)}
                      className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                      title="Edit"
                    >
                      <Edit2 className="w-5 h-5 text-gray-600" />
                    </button>
                    <button 
                      onClick={() => {
                        if (window.confirm(`Delete workflow "${workflow.name}"?`)) {
                          setWorkflows(prev => prev.filter(w => w.id !== workflow.id));
                        }
                      }}
                      className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                      title="Delete"
                    >
                      <Trash2 className="w-5 h-5 text-red-600" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Workflow Builder Preview */}
          <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl border-2 border-dashed border-blue-300 p-8 text-center">
            <Zap className="w-12 h-12 text-blue-600 mx-auto mb-3" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Visual Workflow Builder</h3>
            <p className="text-gray-600 mb-4">Drag and drop to create custom automation workflows</p>
            <button
              onClick={() => setShowWorkflowBuilder(true)}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all font-medium"
            >
              Open Workflow Builder
            </button>
          </div>

          {/* Workflow Editor Modal */}
          {editingWorkflow && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-6">
              <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto p-8">
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-2xl font-bold text-gray-900">
                    {editingWorkflow.isNew ? 'Create New Workflow' : `Edit ${editingWorkflow.name}`}
                  </h3>
                  <button 
                    onClick={() => setEditingWorkflow(null)}
                    className="p-2 hover:bg-gray-100 rounded-lg"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Workflow Name</label>
                    <input
                      type="text"
                      value={editingWorkflow.name}
                      onChange={(e) => setEditingWorkflow({...editingWorkflow, name: e.target.value})}
                      placeholder="e.g., Smart Document Summarization"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                    <textarea
                      value={editingWorkflow.description}
                      onChange={(e) => setEditingWorkflow({...editingWorkflow, description: e.target.value})}
                      placeholder="What does this workflow do?"
                      rows={3}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
                    <select
                      value={editingWorkflow.category}
                      onChange={(e) => setEditingWorkflow({...editingWorkflow, category: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
                    >
                      <option>Chat Enhancement</option>
                      <option>Document Processing</option>
                      <option>Integration</option>
                      <option>Performance</option>
                      <option>Analytics</option>
                      <option>Productivity</option>
                      <option>Notifications</option>
                    </select>
                  </div>

                  <div className="border-t border-gray-200 pt-6">
                    <h4 className="font-semibold text-gray-900 mb-4">Workflow Configuration</h4>
                    
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Trigger (When to run)</label>
                        <textarea
                          value={editingWorkflow.trigger}
                          onChange={(e) => setEditingWorkflow({...editingWorkflow, trigger: e.target.value})}
                          placeholder="e.g., User uploads document in chat"
                          rows={2}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Action (What to do)</label>
                        <textarea
                          value={editingWorkflow.action}
                          onChange={(e) => setEditingWorkflow({...editingWorkflow, action: e.target.value})}
                          placeholder="e.g., Extract text → Generate summary → Return in chat"
                          rows={2}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div>
                      <div className="font-medium text-gray-900">Active Status</div>
                      <div className="text-sm text-gray-500">Enable this workflow</div>
                    </div>
                    <button 
                      onClick={() => setEditingWorkflow({...editingWorkflow, status: editingWorkflow.status === 'active' ? 'paused' : 'active'})}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-all ${
                        editingWorkflow.status === 'active' ? 'bg-blue-600' : 'bg-gray-300'
                      }`}
                    >
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-all ${
                        editingWorkflow.status === 'active' ? 'translate-x-6' : 'translate-x-1'
                      }`}></span>
                    </button>
                  </div>

                  <div className="flex space-x-3 pt-4">
                    <button
                      onClick={() => {
                        if (editingWorkflow.isNew) {
                          setWorkflows(prev => [...prev, { ...editingWorkflow, isNew: false }]);
                        } else {
                          setWorkflows(prev => prev.map(w => 
                            w.id === editingWorkflow.id ? editingWorkflow : w
                          ));
                        }
                        setEditingWorkflow(null);
                      }}
                      disabled={!editingWorkflow.name || !editingWorkflow.trigger || !editingWorkflow.action}
                      className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {editingWorkflow.isNew ? 'Create Workflow' : 'Save Changes'}
                    </button>
                    <button
                      onClick={() => setEditingWorkflow(null)}
                      className="px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-all font-semibold"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Visual Workflow Builder Modal */}
          {showWorkflowBuilder && (
            <VisualWorkflowBuilder
              onClose={() => setShowWorkflowBuilder(false)}
              workflowNodes={workflowNodes}
              setWorkflowNodes={setWorkflowNodes}
              workflowConnections={workflowConnections}
              setWorkflowConnections={setWorkflowConnections}
              selectedNode={selectedNode}
              setSelectedNode={setSelectedNode}
              draggedNode={draggedNode}
              setDraggedNode={setDraggedNode}
              dragOffset={dragOffset}
              setDragOffset={setDragOffset}
            />
          )}
        </div>
      </div>
    );
  };

  // Visual Workflow Builder Component
  const VisualWorkflowBuilder = ({
    onClose,
    workflowNodes,
    setWorkflowNodes,
    workflowConnections,
    setWorkflowConnections,
    selectedNode,
    setSelectedNode,
    draggedNode,
    setDraggedNode,
    dragOffset,
    setDragOffset
  }) => {
    const canvasRef = useRef(null);
    const [connectionStart, setConnectionStart] = useState(null);
    const [isConnecting, setIsConnecting] = useState(false);
    const [mousePos, setMousePos] = useState({ x: 0, y: 0 });

    // Available node types
    const nodeTypes = [
      { id: 'trigger', name: 'Trigger', icon: '🔵', color: 'bg-blue-500', description: 'Start workflow' },
      { id: 'condition', name: 'Condition', icon: '🔶', color: 'bg-orange-500', description: 'Check condition' },
      { id: 'action', name: 'Action', icon: '🟢', color: 'bg-green-500', description: 'Perform action' },
      { id: 'delay', name: 'Delay', icon: '⏱️', color: 'bg-purple-500', description: 'Wait period' },
      { id: 'transform', name: 'Transform', icon: '🔄', color: 'bg-indigo-500', description: 'Transform data' },
      { id: 'end', name: 'End', icon: '🔴', color: 'bg-red-500', description: 'End workflow' }
    ];

    // Add new node to canvas
    const addNode = (nodeType) => {
      const canvasRect = canvasRef.current?.getBoundingClientRect();
      const newNode = {
        id: Date.now().toString(),
        type: nodeType.id,
        name: nodeType.name,
        icon: nodeType.icon,
        color: nodeType.color,
        x: canvasRect ? canvasRect.width / 2 - 75 + Math.random() * 100 : 300,
        y: canvasRect ? canvasRect.height / 2 - 40 + Math.random() * 100 : 200,
        config: {}
      };
      setWorkflowNodes(prev => [...prev, newNode]);
    };

    // Handle node drag
    const handleMouseDown = (e, node) => {
      if (e.button !== 0) return; // Only left click
      e.preventDefault();

      const rect = canvasRef.current?.getBoundingClientRect();
      setDragOffset({
        x: e.clientX - rect.left - node.x,
        y: e.clientY - rect.top - node.y
      });
      setDraggedNode(node);
    };

    const handleMouseMove = (e) => {
      if (draggedNode && canvasRef.current) {
        const rect = canvasRef.current.getBoundingClientRect();
        const newX = e.clientX - rect.left - dragOffset.x;
        const newY = e.clientY - rect.top - dragOffset.y;

        setWorkflowNodes(prev =>
          prev.map(node =>
            node.id === draggedNode.id
              ? { ...node, x: Math.max(0, Math.min(rect.width - 150, newX)), y: Math.max(0, Math.min(rect.height - 80, newY)) }
              : node
          )
        );
      }

      if (isConnecting) {
        const rect = canvasRef.current?.getBoundingClientRect();
        setMousePos({
          x: e.clientX - rect.left,
          y: e.clientY - rect.top
        });
      }
    };

    const handleMouseUp = () => {
      setDraggedNode(null);
      setDragOffset({ x: 0, y: 0 });
    };

    // Connection handling
    const startConnection = (nodeId) => {
      setConnectionStart(nodeId);
      setIsConnecting(true);
    };

    const completeConnection = (endNodeId) => {
      if (connectionStart && connectionStart !== endNodeId) {
        const newConnection = {
          id: Date.now().toString(),
          from: connectionStart,
          to: endNodeId
        };
        setWorkflowConnections(prev => [...prev, newConnection]);
      }
      setConnectionStart(null);
      setIsConnecting(false);
    };

    // Delete node
    const deleteNode = (nodeId) => {
      setWorkflowNodes(prev => prev.filter(node => node.id !== nodeId));
      setWorkflowConnections(prev => prev.filter(conn => conn.from !== nodeId && conn.to !== nodeId));
    };

    // Draw connections
    const renderConnections = () => {
      return workflowConnections.map(conn => {
        const fromNode = workflowNodes.find(n => n.id === conn.from);
        const toNode = workflowNodes.find(n => n.id === conn.to);

        if (!fromNode || !toNode) return null;

        const fromX = fromNode.x + 75;
        const fromY = fromNode.y + 40;
        const toX = toNode.x + 75;
        const toY = toNode.y + 40;

        return (
          <svg
            key={conn.id}
            className="absolute inset-0 pointer-events-none"
            style={{ width: '100%', height: '100%' }}
          >
            <defs>
              <marker
                id={`arrowhead-${conn.id}`}
                markerWidth="10"
                markerHeight="7"
                refX="10"
                refY="3.5"
                orient="auto"
              >
                <polygon
                  points="0 0, 10 3.5, 0 7"
                  fill="#6B7280"
                />
              </marker>
            </defs>
            <path
              d={`M ${fromX} ${fromY} Q ${(fromX + toX) / 2} ${fromY - 50} ${toX} ${toY}`}
              stroke="#6B7280"
              strokeWidth="2"
              fill="none"
              markerEnd={`url(#arrowhead-${conn.id})`}
            />
          </svg>
        );
      });
    };

    // Clear all nodes and connections
    const clearCanvas = () => {
      if (window.confirm('Clear all nodes and connections?')) {
        setWorkflowNodes([]);
        setWorkflowConnections([]);
        setSelectedNode(null);
      }
    };

    // Save workflow
    const saveWorkflow = () => {
      const workflowData = {
        nodes: workflowNodes,
        connections: workflowConnections,
        name: `Workflow_${new Date().toISOString().slice(0, 10)}`,
        created: new Date()
      };
      console.log('Saved workflow:', workflowData);
      alert('Workflow saved successfully!');
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex z-50">
        {/* Sidebar with node palette */}
        <div className="w-80 bg-white shadow-xl flex flex-col">
          <div className="p-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900">Visual Workflow Builder</h2>
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          <div className="flex-1 p-4 overflow-y-auto">
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Node Types</h3>
              <div className="space-y-2">
                {nodeTypes.map(nodeType => (
                  <button
                    key={nodeType.id}
                    onClick={() => addNode(nodeType)}
                    className="w-full p-3 text-left border border-gray-200 rounded-lg hover:border-blue-300 hover:bg-blue-50 transition-all group"
                  >
                    <div className="flex items-center space-x-3">
                      <div className={`w-8 h-8 ${nodeType.color} rounded-lg flex items-center justify-center text-white text-sm`}>
                        {nodeType.icon}
                      </div>
                      <div>
                        <div className="font-medium text-gray-900">{nodeType.name}</div>
                        <div className="text-xs text-gray-500">{nodeType.description}</div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {selectedNode && (
              <div className="border border-gray-200 rounded-lg p-4">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">Node Properties</h3>
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Name</label>
                    <input
                      type="text"
                      value={selectedNode.name}
                      onChange={(e) => {
                        const updatedNode = { ...selectedNode, name: e.target.value };
                        setSelectedNode(updatedNode);
                        setWorkflowNodes(prev => prev.map(n => n.id === updatedNode.id ? updatedNode : n));
                      }}
                      className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Type</label>
                    <div className="text-sm text-gray-900">{selectedNode.type}</div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="p-4 border-t border-gray-200 space-y-2">
            <button
              onClick={saveWorkflow}
              disabled={workflowNodes.length === 0}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Save Workflow
            </button>
            <button
              onClick={clearCanvas}
              disabled={workflowNodes.length === 0}
              className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-all font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Clear Canvas
            </button>
          </div>
        </div>

        {/* Main canvas area */}
        <div className="flex-1 bg-gray-100 relative overflow-hidden">
          <div
            ref={canvasRef}
            className="w-full h-full relative cursor-crosshair"
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
          >
            {/* Grid background */}
            <div
              className="absolute inset-0 opacity-30"
              style={{
                backgroundImage: `
                  linear-gradient(#e5e7eb 1px, transparent 1px),
                  linear-gradient(90deg, #e5e7eb 1px, transparent 1px)
                `,
                backgroundSize: '20px 20px'
              }}
            />

            {/* Render connections */}
            {renderConnections()}

            {/* Temporary connection line while dragging */}
            {isConnecting && connectionStart && (
              <svg className="absolute inset-0 pointer-events-none" style={{ width: '100%', height: '100%' }}>
                <path
                  d={`M ${workflowNodes.find(n => n.id === connectionStart)?.x + 75} ${workflowNodes.find(n => n.id === connectionStart)?.y + 40} L ${mousePos.x} ${mousePos.y}`}
                  stroke="#3B82F6"
                  strokeWidth="2"
                  strokeDasharray="5,5"
                  fill="none"
                />
              </svg>
            )}

            {/* Render nodes */}
            {workflowNodes.map(node => (
              <div
                key={node.id}
                className={`absolute bg-white border-2 rounded-lg shadow-lg cursor-move select-none transition-all ${
                  selectedNode?.id === node.id ? 'border-blue-500 shadow-blue-200' : 'border-gray-200 hover:border-gray-300'
                }`}
                style={{
                  left: `${node.x}px`,
                  top: `${node.y}px`,
                  width: '150px',
                  height: '80px'
                }}
                onMouseDown={(e) => handleMouseDown(e, node)}
                onClick={() => setSelectedNode(node)}
              >
                <div className="p-3 h-full flex flex-col">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-2">
                      <div className={`w-6 h-6 ${node.color} rounded flex items-center justify-center text-white text-xs`}>
                        {node.icon}
                      </div>
                      <div className="text-sm font-medium text-gray-900 truncate">{node.name}</div>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteNode(node.id);
                      }}
                      className="w-4 h-4 text-gray-400 hover:text-red-500 transition-colors"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>

                  <div className="flex items-center justify-between mt-auto">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        startConnection(node.id);
                      }}
                      className="w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center hover:bg-blue-200 transition-colors text-xs"
                      title="Start connection"
                    >
                      →
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (isConnecting) {
                          completeConnection(node.id);
                        }
                      }}
                      className={`w-6 h-6 rounded-full flex items-center justify-center transition-colors text-xs ${
                        isConnecting && connectionStart !== node.id
                          ? 'bg-green-100 text-green-600 hover:bg-green-200'
                          : 'bg-gray-100 text-gray-400'
                      }`}
                      title="End connection"
                    >
                      ◉
                    </button>
                  </div>
                </div>
              </div>
            ))}

            {/* Instructions overlay when empty */}
            {workflowNodes.length === 0 && (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center p-8 bg-white rounded-xl shadow-lg border border-gray-200">
                  <Zap className="w-12 h-12 text-blue-600 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Create Your First Workflow</h3>
                  <p className="text-gray-600 mb-4">Drag node types from the sidebar to start building</p>
                  <div className="text-sm text-gray-500">
                    • Click nodes to select and configure them<br/>
                    • Use → and ◉ buttons to connect nodes<br/>
                    • Drag nodes to reposition them
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // User Access Management View
  const UserAccessManagement = () => {
    const [groupAccessMatrix, setGroupAccessMatrix] = useState({});
    const [bulkEditMode, setBulkEditMode] = useState(false);
    const [selectedGroups, setSelectedGroups] = useState([]);
    const [selectedItems, setSelectedItems] = useState([]);
    const [viewMode, setViewMode] = useState('data-source'); // 'data-source' or 'folder-level'

    // Flatten all accessible items (folders, channels, spaces) from data sources
    const getAllAccessibleItems = () => {
      const items = [];
      dataSources.forEach(ds => {
        if (ds.status === 'connected') {
          if (ds.config?.folders) {
            ds.config.folders.forEach(folder => {
              items.push({
                id: `${ds.id}-folder-${folder}`,
                dataSourceId: ds.id,
                dataSourceName: ds.name,
                dataSourceIcon: ds.icon,
                type: 'folder',
                name: folder,
                fullName: `${ds.name} > ${folder}`
              });
            });
          }
          if (ds.config?.channels) {
            ds.config.channels.forEach(channel => {
              items.push({
                id: `${ds.id}-channel-${channel}`,
                dataSourceId: ds.id,
                dataSourceName: ds.name,
                dataSourceIcon: ds.icon,
                type: 'channel',
                name: channel,
                fullName: `${ds.name} > ${channel}`
              });
            });
          }
          if (ds.config?.spaces) {
            ds.config.spaces.forEach(space => {
              items.push({
                id: `${ds.id}-space-${space}`,
                dataSourceId: ds.id,
                dataSourceName: ds.name,
                dataSourceIcon: ds.icon,
                type: 'space',
                name: space,
                fullName: `${ds.name} > ${space}`
              });
            });
          }
          if (ds.config?.workspaces) {
            ds.config.workspaces.forEach(workspace => {
              items.push({
                id: `${ds.id}-workspace-${workspace}`,
                dataSourceId: ds.id,
                dataSourceName: ds.name,
                dataSourceIcon: ds.icon,
                type: 'workspace',
                name: workspace,
                fullName: `${ds.name} > ${workspace}`
              });
            });
          }
          // For data sources without sub-items, add the whole source
          if (!ds.config?.folders && !ds.config?.channels && !ds.config?.spaces && !ds.config?.workspaces) {
            items.push({
              id: `${ds.id}-full`,
              dataSourceId: ds.id,
              dataSourceName: ds.name,
              dataSourceIcon: ds.icon,
              type: 'full-source',
              name: ds.name,
              fullName: ds.name
            });
          }
        }
      });
      return items;
    };

    const accessibleItems = getAllAccessibleItems();

    // Initialize access matrix based on existing groups and accessible items
    useEffect(() => {
      const matrix = {};
      userGroups.forEach(group => {
        matrix[group.id] = {};

        if (viewMode === 'data-source') {
          // Data source level access
          dataSources.forEach(ds => {
            // Define group access patterns based on group type
            matrix[group.id][ds.id] =
              group.name === 'Administrators' || // Admins get all access
              (group.name === 'Sales Team' && (ds.name.includes('Drive') || ds.name.includes('Slack'))) ||
              (group.name === 'Engineering' && (ds.name.includes('Confluence') || ds.name.includes('Slack'))) ||
              (group.name === 'HR Department' && (ds.name.includes('Drive') || ds.name.includes('Confluence'))) ||
              (group.name === 'External Consultants' && ds.name.includes('Document Folder'));
          });
        } else {
          // Folder/channel level access
          accessibleItems.forEach(item => {
            // More granular access based on group and item type
            matrix[group.id][item.id] =
              group.name === 'Administrators' || // Admins get all access
              (group.name === 'Sales Team' && (item.name.includes('Sales') || item.name.includes('sales'))) ||
              (group.name === 'Engineering' && (item.name.includes('Engineering') || item.name.includes('engineering'))) ||
              (group.name === 'HR Department' && (item.name.includes('HR') || item.name.includes('Product'))) ||
              (group.name === 'External Consultants' && item.type === 'full-source');
          });
        }
      });
      setGroupAccessMatrix(matrix);
    }, [userGroups, dataSources, viewMode]);

    const toggleAccess = (groupId, itemId) => {
      setGroupAccessMatrix(prev => ({
        ...prev,
        [groupId]: {
          ...prev[groupId],
          [itemId]: !prev[groupId]?.[itemId]
        }
      }));
    };

    const bulkGrantAccess = () => {
      if (selectedGroups.length === 0 || selectedItems.length === 0) return;

      setGroupAccessMatrix(prev => {
        const updated = { ...prev };
        selectedGroups.forEach(groupId => {
          selectedItems.forEach(itemId => {
            updated[groupId] = { ...updated[groupId], [itemId]: true };
          });
        });
        return updated;
      });

      setSelectedGroups([]);
      setSelectedItems([]);
      setBulkEditMode(false);
    };

    const bulkRevokeAccess = () => {
      if (selectedGroups.length === 0 || selectedItems.length === 0) return;

      setGroupAccessMatrix(prev => {
        const updated = { ...prev };
        selectedGroups.forEach(groupId => {
          selectedItems.forEach(itemId => {
            updated[groupId] = { ...updated[groupId], [itemId]: false };
          });
        });
        return updated;
      });

      setSelectedGroups([]);
      setSelectedItems([]);
      setBulkEditMode(false);
    };

    const getGroupAccessCount = (groupId) => {
      return Object.values(groupAccessMatrix[groupId] || {}).filter(Boolean).length;
    };

    const getDataSourceGroupCount = (dsId) => {
      if (viewMode === 'data-source') {
        return Object.keys(groupAccessMatrix).filter(groupId =>
          groupAccessMatrix[groupId]?.[dsId]
        ).length;
      } else {
        // For folder-level view, count groups who have access to any item in this data source
        const itemsInDataSource = accessibleItems.filter(item => item.dataSourceId === dsId);
        return Object.keys(groupAccessMatrix).filter(groupId =>
          itemsInDataSource.some(item => groupAccessMatrix[groupId]?.[item.id])
        ).length;
      }
    };

    const getItemGroupCount = (itemId) => {
      return Object.keys(groupAccessMatrix).filter(groupId =>
        groupAccessMatrix[groupId]?.[itemId]
      ).length;
    };

    const getGroupColor = (color) => {
      const colors = {
        'red': 'bg-red-100 text-red-800',
        'green': 'bg-green-100 text-green-800',
        'blue': 'bg-blue-100 text-blue-800',
        'purple': 'bg-purple-100 text-purple-800',
        'gray': 'bg-gray-100 text-gray-800'
      };
      return colors[color] || 'bg-gray-100 text-gray-800';
    };

    return (
      <div className="flex-1 overflow-y-auto bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Group Access Management</h2>
              <p className="text-gray-600 mt-1">
                {viewMode === 'data-source'
                  ? 'Manage which user groups can access which data sources'
                  : 'Manage granular group access to folders, channels, and spaces'}
              </p>
            </div>
            <div className="flex items-center space-x-3">
              {/* View Mode Toggle */}
              <div className="flex bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => setViewMode('data-source')}
                  className={`px-3 py-1 rounded-md text-sm font-medium transition-all ${
                    viewMode === 'data-source'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Data Sources
                </button>
                <button
                  onClick={() => setViewMode('folder-level')}
                  className={`px-3 py-1 rounded-md text-sm font-medium transition-all ${
                    viewMode === 'folder-level'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Folders & Channels
                </button>
              </div>

              <button
                onClick={() => setBulkEditMode(!bulkEditMode)}
                className={`px-4 py-2 rounded-lg font-medium transition-all ${
                  bulkEditMode
                    ? 'bg-orange-600 text-white hover:bg-orange-700'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                {bulkEditMode ? 'Exit Bulk Edit' : 'Bulk Edit Mode'}
              </button>
              {bulkEditMode && (
                <>
                  <button
                    onClick={bulkGrantAccess}
                    disabled={selectedGroups.length === 0 || selectedItems.length === 0}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Grant Access
                  </button>
                  <button
                    onClick={bulkRevokeAccess}
                    disabled={selectedGroups.length === 0 || selectedItems.length === 0}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Revoke Access
                  </button>
                </>
              )}
            </div>
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-4 gap-4 mb-6">
            <div className="bg-white rounded-xl p-4 border border-gray-200">
              <div className="text-sm text-gray-600 mb-1">Total Groups</div>
              <div className="text-2xl font-bold text-gray-900">{userGroups.length}</div>
            </div>
            <div className="bg-white rounded-xl p-4 border border-gray-200">
              <div className="text-sm text-gray-600 mb-1">
                {viewMode === 'data-source' ? 'Data Sources' : 'Accessible Items'}
              </div>
              <div className="text-2xl font-bold text-gray-900">
                {viewMode === 'data-source' ? dataSources.length : accessibleItems.length}
              </div>
            </div>
            <div className="bg-white rounded-xl p-4 border border-gray-200">
              <div className="text-sm text-gray-600 mb-1">Active Permissions</div>
              <div className="text-2xl font-bold text-green-600">
                {Object.values(groupAccessMatrix).reduce((total, groupAccess) =>
                  total + Object.values(groupAccess).filter(Boolean).length, 0
                )}
              </div>
            </div>
            <div className="bg-white rounded-xl p-4 border border-gray-200">
              <div className="text-sm text-gray-600 mb-1">Avg Access per Group</div>
              <div className="text-2xl font-bold text-blue-600">
                {userGroups.length > 0 ? Math.round(
                  Object.values(groupAccessMatrix).reduce((total, groupAccess) =>
                    total + Object.values(groupAccess).filter(Boolean).length, 0
                  ) / userGroups.length * 10
                ) / 10 : 0}
              </div>
            </div>
          </div>

          {bulkEditMode && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
              <h3 className="font-semibold text-amber-900 mb-3">Bulk Edit Mode</h3>
              <p className="text-sm text-amber-800 mb-4">
                Select groups and data sources, then use Grant/Revoke buttons to bulk assign access.
              </p>
              <div className="text-sm text-amber-700">
                Selected: {selectedGroups.length} groups, {selectedItems.length} {viewMode === 'data-source' ? 'data sources' : 'items'}
              </div>
            </div>
          )}

          {/* Access Matrix */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
              <h3 className="font-semibold text-gray-900">
                {viewMode === 'data-source' ? 'Group-to-Data Source Access Matrix' : 'Group-to-Folder/Channel Access Matrix'}
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                {viewMode === 'data-source'
                  ? 'Toggle group access to data sources or use bulk operations'
                  : 'Toggle group access to specific folders, channels, and spaces'}
              </p>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-200">
                      Group
                    </th>
                    {viewMode === 'data-source' ? (
                      // Data Source headers
                      dataSources.map(ds => (
                        <th key={ds.id} className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-200 min-w-32">
                          <div className="space-y-1">
                            <div className="flex items-center justify-center space-x-1">
                              {bulkEditMode && (
                                <input
                                  type="checkbox"
                                  checked={selectedItems.includes(ds.id)}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setSelectedItems(prev => [...prev, ds.id]);
                                    } else {
                                      setSelectedItems(prev => prev.filter(id => id !== ds.id));
                                    }
                                  }}
                                  className="w-3 h-3 text-blue-600 rounded"
                                />
                              )}
                              <span className="text-xl">{ds.icon}</span>
                            </div>
                            <div className="font-medium text-gray-900 text-xs">{ds.name}</div>
                            <div className="text-xs text-gray-500">
                              {getDataSourceGroupCount(ds.id)} groups
                            </div>
                          </div>
                        </th>
                      ))
                    ) : (
                      // Folder/Channel headers
                      accessibleItems.map(item => (
                        <th key={item.id} className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-200 min-w-40">
                          <div className="space-y-1">
                            <div className="flex items-center justify-center space-x-1">
                              {bulkEditMode && (
                                <input
                                  type="checkbox"
                                  checked={selectedItems.includes(item.id)}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setSelectedItems(prev => [...prev, item.id]);
                                    } else {
                                      setSelectedItems(prev => prev.filter(id => id !== item.id));
                                    }
                                  }}
                                  className="w-3 h-3 text-blue-600 rounded"
                                />
                              )}
                              <span className="text-lg">{item.dataSourceIcon}</span>
                            </div>
                            <div className="font-medium text-gray-900 text-xs">{item.name}</div>
                            <div className="text-xs text-gray-400">{item.dataSourceName}</div>
                            <div className="text-xs text-gray-500">
                              {getItemGroupCount(item.id)} groups
                            </div>
                          </div>
                        </th>
                      ))
                    )}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {userGroups.map(group => (
                    <tr key={group.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap border-r border-gray-200">
                        <div className="flex items-center space-x-3">
                          {bulkEditMode && (
                            <input
                              type="checkbox"
                              checked={selectedGroups.includes(group.id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedGroups(prev => [...prev, group.id]);
                                } else {
                                  setSelectedGroups(prev => prev.filter(id => id !== group.id));
                                }
                              }}
                              className="w-4 h-4 text-blue-600 rounded"
                            />
                          )}
                          <div className="flex items-center space-x-3">
                            <div className="text-2xl">{group.icon}</div>
                            <div>
                              <div className="font-medium text-gray-900">{group.name}</div>
                              <div className="text-sm text-gray-500">{group.description}</div>
                              <div className="flex items-center space-x-2 mt-1">
                                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getGroupColor(group.color)}`}>
                                  {group.memberCount} members
                                </span>
                                <span className="text-xs text-gray-500">
                                  {getGroupAccessCount(group.id)} / {viewMode === 'data-source' ? dataSources.length : accessibleItems.length} {viewMode === 'data-source' ? 'sources' : 'items'}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </td>
                      {viewMode === 'data-source' ? (
                        // Data Source cells
                        dataSources.map(ds => (
                          <td key={`${group.id}-${ds.id}`} className="px-3 py-4 text-center border-r border-gray-200">
                            <button
                              onClick={() => toggleAccess(group.id, ds.id)}
                              className={`w-8 h-8 rounded-full flex items-center justify-center transition-all ${
                                groupAccessMatrix[group.id]?.[ds.id]
                                  ? 'bg-green-100 text-green-600 hover:bg-green-200'
                                  : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
                              }`}
                              title={`${groupAccessMatrix[group.id]?.[ds.id] ? 'Revoke' : 'Grant'} ${group.name} access to ${ds.name}`}
                            >
                              {groupAccessMatrix[group.id]?.[ds.id] ? (
                                <Check className="w-4 h-4" />
                              ) : (
                                <X className="w-4 h-4" />
                              )}
                            </button>
                          </td>
                        ))
                      ) : (
                        // Folder/Channel cells
                        accessibleItems.map(item => (
                          <td key={`${group.id}-${item.id}`} className="px-3 py-4 text-center border-r border-gray-200">
                            <button
                              onClick={() => toggleAccess(group.id, item.id)}
                              className={`w-8 h-8 rounded-full flex items-center justify-center transition-all ${
                                groupAccessMatrix[group.id]?.[item.id]
                                  ? 'bg-green-100 text-green-600 hover:bg-green-200'
                                  : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
                              }`}
                              title={`${groupAccessMatrix[group.id]?.[item.id] ? 'Revoke' : 'Grant'} ${group.name} access to ${item.fullName}`}
                            >
                              {groupAccessMatrix[group.id]?.[item.id] ? (
                                <Check className="w-4 h-4" />
                              ) : (
                                <X className="w-4 h-4" />
                              )}
                            </button>
                          </td>
                        ))
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Data Source Management Actions */}
          <div className="mt-6 grid grid-cols-2 gap-6">
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="font-semibold text-gray-900 mb-4">Data Source Overview</h3>
              <p className="text-sm text-gray-600 mb-4">Current status of configured data sources</p>
              <div className="space-y-3">
                {dataSources.map(ds => (
                  <div key={ds.id} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <span className="text-xl">{ds.icon}</span>
                      <div>
                        <div className="font-medium text-gray-900">{ds.name}</div>
                        <div className="text-xs text-gray-500">
                          {ds.files.toLocaleString()} files • {ds.status}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        ds.status === 'connected' ? 'bg-green-100 text-green-700' :
                        ds.status === 'syncing' ? 'bg-blue-100 text-blue-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {getDataSourceGroupCount(ds.id)} groups
                      </span>
                      <button
                        onClick={() => setCurrentView('sources')}
                        className="text-blue-600 hover:text-blue-700 text-sm"
                      >
                        Configure
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="font-semibold text-gray-900 mb-4">Access Analytics</h3>
              <p className="text-sm text-gray-600 mb-4">Group access patterns across data sources</p>
              <div className="space-y-4">
                {/* Most accessed sources */}
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Most Accessed Sources</h4>
                  {dataSources
                    .sort((a, b) => getDataSourceGroupCount(b.id) - getDataSourceGroupCount(a.id))
                    .slice(0, 3)
                    .map(ds => (
                      <div key={ds.id} className="flex items-center justify-between py-2">
                        <div className="flex items-center space-x-2">
                          <span>{ds.icon}</span>
                          <span className="text-sm text-gray-900">{ds.name}</span>
                        </div>
                        <span className="text-sm text-gray-600">{getDataSourceGroupCount(ds.id)} groups</span>
                      </div>
                    ))
                  }
                </div>

                {/* Groups with most access */}
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Groups with Most Access</h4>
                  {userGroups
                    .sort((a, b) => getGroupAccessCount(b.id) - getGroupAccessCount(a.id))
                    .slice(0, 3)
                    .map(group => (
                      <div key={group.id} className="flex items-center justify-between py-2">
                        <div className="flex items-center space-x-2">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getGroupColor(group.color)}`}>
                            {group.icon}
                          </span>
                          <span className="text-sm text-gray-900">{group.name}</span>
                        </div>
                        <span className="text-sm text-gray-600">{getGroupAccessCount(group.id)} sources</span>
                      </div>
                    ))
                  }
                </div>

                {/* Quick actions */}
                <div className="pt-3 border-t border-gray-200">
                  <button
                    onClick={() => setCurrentView('sources')}
                    className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
                  >
                    Manage Data Sources
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Users View
  const UsersView = () => (
    <div className="flex-1 overflow-y-auto bg-gray-50 p-6">
      <div className="max-w-6xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Team Members</h2>
            <p className="text-gray-600 mt-1">Manage users and permissions</p>
          </div>
          <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all flex items-center space-x-2">
            <Plus className="w-4 h-4" />
            <span>Invite User</span>
          </button>
        </div>

        {/* User Stats */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-xl p-4 border border-gray-200">
            <div className="text-sm text-gray-600 mb-1">Total Users</div>
            <div className="text-2xl font-bold text-gray-900">{users.length}</div>
          </div>
          <div className="bg-white rounded-xl p-4 border border-gray-200">
            <div className="text-sm text-gray-600 mb-1">Online Now</div>
            <div className="text-2xl font-bold text-green-600">{users.filter(u => u.status === 'online').length}</div>
          </div>
          <div className="bg-white rounded-xl p-4 border border-gray-200">
            <div className="text-sm text-gray-600 mb-1">Total Queries</div>
            <div className="text-2xl font-bold text-gray-900">{users.reduce((acc, u) => acc + u.queries, 0).toLocaleString()}</div>
          </div>
          <div className="bg-white rounded-xl p-4 border border-gray-200">
            <div className="text-sm text-gray-600 mb-1">Admins</div>
            <div className="text-2xl font-bold text-gray-900">{users.filter(u => u.role === 'Admin').length}</div>
          </div>
        </div>

        {/* Users Table */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Queries</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Active</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {users.map(user => (
                <tr key={user.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-semibold">
                        {user.name.split(' ').map(n => n[0]).join('')}
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">{user.name}</div>
                        <div className="text-sm text-gray-500">{user.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      user.role === 'Admin' ? 'bg-purple-100 text-purple-700' :
                      user.role === 'Member' ? 'bg-blue-100 text-blue-700' :
                      'bg-gray-100 text-gray-700'
                    }`}>
                      {user.role}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{user.queries.toLocaleString()}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{user.lastActive}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center ${
                      user.status === 'online' ? 'text-green-600' : 'text-gray-400'
                    }`}>
                      <span className={`w-2 h-2 rounded-full mr-2 ${
                        user.status === 'online' ? 'bg-green-600' : 'bg-gray-400'
                      }`}></span>
                      <span className="text-xs font-medium">{user.status}</span>
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button className="text-blue-600 hover:text-blue-800 mr-3">Edit</button>
                    <button className="text-red-600 hover:text-red-800">Remove</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  // Assistants View
  const AssistantsView = () => (
    <div className="flex-1 overflow-y-auto bg-gray-50 p-6">
      <div className="max-w-6xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Chat Assistants</h2>
            <p className="text-gray-600 mt-1">Create specialized assistants with custom data access</p>
          </div>
          <button 
            onClick={() => setEditingAssistant({ 
              id: Date.now(), 
              name: '', 
              description: '', 
              icon: '🤖', 
              color: 'blue',
              dataSources: [], 
              isActive: true,
              isNew: true
            })}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all flex items-center space-x-2"
          >
            <Plus className="w-4 h-4" />
            <span>Create Assistant</span>
          </button>
        </div>

        {/* Assistant Stats */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-xl p-4 border border-gray-200">
            <div className="text-sm text-gray-600 mb-1">Total Assistants</div>
            <div className="text-2xl font-bold text-gray-900">{assistants.length}</div>
          </div>
          <div className="bg-white rounded-xl p-4 border border-gray-200">
            <div className="text-sm text-gray-600 mb-1">Active</div>
            <div className="text-2xl font-bold text-green-600">{assistants.filter(a => a.isActive).length}</div>
          </div>
          <div className="bg-white rounded-xl p-4 border border-gray-200">
            <div className="text-sm text-gray-600 mb-1">Total Queries</div>
            <div className="text-2xl font-bold text-gray-900">{queryHistory.length.toLocaleString()}</div>
          </div>
          <div className="bg-white rounded-xl p-4 border border-gray-200">
            <div className="text-sm text-gray-600 mb-1">Data Sources</div>
            <div className="text-2xl font-bold text-gray-900">{dataSources.filter(d => d.status === 'connected').length}</div>
          </div>
        </div>

        {/* Assistants Grid */}
        <div className="grid grid-cols-2 gap-6 mb-6">
          {assistants.map(assistant => {
            const assistantSources = dataSources.filter(ds => assistant.dataSources.includes(ds.id));
            const connectedSources = assistantSources.filter(ds => ds.status === 'connected');
            
            return (
              <div key={assistant.id} className="bg-white rounded-xl border-2 border-gray-200 p-6 hover:border-blue-300 transition-all">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-start space-x-4">
                    <div className={`w-16 h-16 bg-gradient-to-br from-${assistant.color}-400 to-${assistant.color}-600 rounded-2xl flex items-center justify-center text-3xl shadow-lg`}>
                      {assistant.icon}
                    </div>
                    <div className="flex-1">
                      <h3 className="font-bold text-gray-900 text-lg mb-1">{assistant.name}</h3>
                      <p className="text-sm text-gray-600 mb-3">{assistant.description}</p>
                      <div className="flex items-center space-x-2">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          assistant.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                        }`}>
                          {assistant.isActive ? '● Active' : '○ Inactive'}
                        </span>
                        <span className="text-xs text-gray-500">
                          {assistant.isDocumentBot
                            ? `${documentStats.get(assistant.id)?.totalFiles || 0} documents monitored`
                            : `${connectedSources.length}/${assistantSources.length} sources active`
                          }
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button 
                      onClick={() => setEditingAssistant(assistant)}
                      className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      <Edit2 className="w-4 h-4 text-gray-600" />
                    </button>
                    <button 
                      onClick={() => {
                        if (window.confirm(`Delete ${assistant.name}?`)) {
                          setAssistants(prev => prev.filter(a => a.id !== assistant.id));
                        }
                      }}
                      className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                    >
                      <Trash2 className="w-4 h-4 text-red-600" />
                    </button>
                  </div>
                </div>

                {/* Connected Data Sources or Folder Path */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="text-xs font-medium text-gray-500 mb-2">
                    {assistant.isDocumentBot ? 'MONITORED FOLDER' : 'CONNECTED DATA SOURCES'}
                  </div>
                  {assistant.isDocumentBot ? (
                    <div className="space-y-2">
                      {assistant.folderPath ? (
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-2">
                            <Folder className="w-4 h-4 text-blue-600" />
                            <span className="text-sm text-gray-700 font-mono">{assistant.folderPath}</span>
                          </div>
                          <span className="text-xs text-green-600 bg-green-100 px-2 py-1 rounded">
                            {documentStats.get(assistant.id)?.totalFiles || 0} files
                          </span>
                        </div>
                      ) : (
                        <span className="text-xs text-gray-500 italic">No folder configured</span>
                      )}
                    </div>
                  ) : assistantSources.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {assistantSources.map(source => (
                        <span 
                          key={source.id} 
                          className={`text-xs px-3 py-1.5 rounded-full ${
                            source.status === 'connected' 
                              ? 'bg-green-100 text-green-700' 
                              : 'bg-gray-200 text-gray-600'
                          }`}
                        >
                          {source.icon} {source.name}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500">No data sources configured</p>
                  )}
                </div>

                {/* Quick Actions */}
                <div className="mt-4 flex space-x-2">
                  <button 
                    onClick={() => {
                      setCurrentAssistant(assistant);
                      setCurrentView('chat');
                    }}
                    className="flex-1 px-4 py-2 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-all text-sm font-medium"
                  >
                    Open Chat
                  </button>
                  <button 
                    onClick={() => setEditingAssistant(assistant)}
                    className="flex-1 px-4 py-2 bg-gray-50 text-gray-700 rounded-lg hover:bg-gray-100 transition-all text-sm font-medium"
                  >
                    Configure
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Assistant Editor Modal */}
        {editingAssistant && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-6">
            <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-8">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-2xl font-bold text-gray-900">
                  {editingAssistant.isNew ? 'Create New Assistant' : `Edit ${editingAssistant.name}`}
                </h3>
                <button 
                  onClick={() => setEditingAssistant(null)}
                  className="p-2 hover:bg-gray-100 rounded-lg"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-6">
                {/* Basic Info */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Assistant Name</label>
                  <input
                    type="text"
                    value={editingAssistant.name}
                    onChange={(e) => setEditingAssistant({...editingAssistant, name: e.target.value})}
                    placeholder="e.g., Sales Assistant"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                  <textarea
                    value={editingAssistant.description}
                    onChange={(e) => setEditingAssistant({...editingAssistant, description: e.target.value})}
                    placeholder="What can this assistant help with?"
                    rows={3}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
                  />
                </div>

                {/* Icon Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Icon</label>
                  <div className="grid grid-cols-8 gap-2">
                    {['🤖', '💼', '👥', '🎧', '📊', '🔬', '💻', '🎨', '📱', '🎯', '🌟', '⚡', '🔍', '📚', '🎓', '🏢'].map(icon => (
                      <button
                        key={icon}
                        onClick={() => setEditingAssistant({...editingAssistant, icon})}
                        className={`p-3 text-2xl border-2 rounded-lg hover:border-blue-500 transition-all ${
                          editingAssistant.icon === icon ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
                        }`}
                      >
                        {icon}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Color Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Color Theme</label>
                  <div className="flex space-x-2">
                    {['blue', 'green', 'purple', 'orange', 'red', 'pink'].map(color => (
                      <button
                        key={color}
                        onClick={() => setEditingAssistant({...editingAssistant, color})}
                        className={`w-12 h-12 rounded-lg bg-gradient-to-br from-${color}-400 to-${color}-600 border-4 transition-all ${
                          editingAssistant.color === color ? 'border-gray-900 scale-110' : 'border-transparent'
                        }`}
                      />
                    ))}
                  </div>
                </div>

                {/* Folder Path for Document Assistants */}
                {editingAssistant.isDocumentBot && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Folder Path to Monitor</label>
                    <input
                      type="text"
                      value={editingAssistant.folderPath || ''}
                      onChange={(e) => setEditingAssistant({...editingAssistant, folderPath: e.target.value})}
                      placeholder="C:\Documents\MyFolder or /home/user/documents"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      The assistant will monitor this folder for PDF, DOCX, TXT, and Excel files
                    </p>
                  </div>
                )}

                {/* Data Sources Selection */}
                {!editingAssistant.isDocumentBot && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Data Sources Access ({editingAssistant.dataSources.length} selected)
                    </label>
                  <div className="border border-gray-300 rounded-lg p-4 max-h-64 overflow-y-auto space-y-2">
                    {dataSources.map(source => (
                      <label key={source.id} className="flex items-center space-x-3 p-3 hover:bg-gray-50 rounded-lg cursor-pointer">
                        <input
                          type="checkbox"
                          checked={editingAssistant.dataSources.includes(source.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setEditingAssistant({
                                ...editingAssistant, 
                                dataSources: [...editingAssistant.dataSources, source.id]
                              });
                            } else {
                              setEditingAssistant({
                                ...editingAssistant, 
                                dataSources: editingAssistant.dataSources.filter(id => id !== source.id)
                              });
                            }
                          }}
                          className="w-5 h-5 text-blue-600 rounded"
                        />
                        <div className="flex items-center space-x-2 flex-1">
                          <span className="text-xl">{source.icon}</span>
                          <div>
                            <div className="font-medium text-gray-900">{source.name}</div>
                            <div className="text-xs text-gray-500">{source.id === 6 && dataSourceStats.has(6) ? (dataSourceStats.get(6).files || 0).toLocaleString() : (source.files || 0).toLocaleString()} files • {source.id === 6 && dataSourceStats.has(6) ? dataSourceStats.get(6).status : source.status}</div>
                          </div>
                        </div>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          source.status === 'connected' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                        }`}>
                          {source.status}
                        </span>
                      </label>
                    ))}
                  </div>
                  </div>
                )}

                {/* Active Status */}
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div>
                    <div className="font-medium text-gray-900">Active Status</div>
                    <div className="text-sm text-gray-500">Make this assistant available to users</div>
                  </div>
                  <button 
                    onClick={() => setEditingAssistant({...editingAssistant, isActive: !editingAssistant.isActive})}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-all ${
                      editingAssistant.isActive ? 'bg-blue-600' : 'bg-gray-300'
                    }`}
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-all ${
                      editingAssistant.isActive ? 'translate-x-6' : 'translate-x-1'
                    }`}></span>
                  </button>
                </div>

                {/* Actions */}
                <div className="flex space-x-3 pt-4">
                  <button
                    onClick={async () => {
                      // Handle folder monitoring for document assistants
                      if (editingAssistant.isDocumentBot && editingAssistant.folderPath) {
                        try {
                          await apiService.startFolderMonitoring(
                            editingAssistant.id,
                            editingAssistant.folderPath
                          );
                        } catch (error) {
                          console.error('Failed to start folder monitoring:', error);
                          alert(`Failed to start folder monitoring: ${error.message}`);
                          return;
                        }
                      }

                      if (editingAssistant.isNew) {
                        setAssistants(prev => [...prev, { ...editingAssistant, isNew: false }]);
                        setMessages(prev => ({
                          ...prev,
                          [editingAssistant.id]: [{
                            role: 'assistant',
                            content: editingAssistant.isDocumentBot
                              ? `Hello! I'm your ${editingAssistant.name}. I'm now monitoring "${editingAssistant.folderPath}" for documents. Add some files to the folder and I'll help you chat with them!`
                              : `Hello! I'm your ${editingAssistant.name}. ${editingAssistant.description}`,
                            timestamp: new Date()
                          }]
                        }));
                      } else {
                        setAssistants(prev => prev.map(a =>
                          a.id === editingAssistant.id ? editingAssistant : a
                        ));
                      }
                      setEditingAssistant(null);
                    }}
                    disabled={!editingAssistant.name || (editingAssistant.isDocumentBot ? !editingAssistant.folderPath : editingAssistant.dataSources.length === 0)}
                    className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {editingAssistant.isNew ? 'Create Assistant' : 'Save Changes'}
                  </button>
                  <button
                    onClick={() => setEditingAssistant(null)}
                    className="px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-all font-semibold"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  // Query History View
  const QueryHistoryView = () => (
    <div className="flex-1 overflow-y-auto bg-gray-50 p-6">
      <div className="max-w-6xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Query History</h2>
            <p className="text-gray-600 mt-1">Track all user queries and AI responses</p>
          </div>
          <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all flex items-center space-x-2">
            <Download className="w-4 h-4" />
            <span>Export CSV</span>
          </button>
        </div>

        {/* Query Stats */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-xl p-4 border border-gray-200">
            <div className="text-sm text-gray-600 mb-1">Today's Queries</div>
            <div className="text-2xl font-bold text-gray-900">1,247</div>
          </div>
          <div className="bg-white rounded-xl p-4 border border-gray-200">
            <div className="text-sm text-gray-600 mb-1">This Month</div>
            <div className="text-2xl font-bold text-gray-900">12,450</div>
          </div>
          <div className="bg-white rounded-xl p-4 border border-gray-200">
            <div className="text-sm text-gray-600 mb-1">Avg Response Time</div>
            <div className="text-2xl font-bold text-gray-900">1.2s</div>
          </div>
          <div className="bg-white rounded-xl p-4 border border-gray-200">
            <div className="text-sm text-gray-600 mb-1">Tokens Used</div>
            <div className="text-2xl font-bold text-gray-900">2.4M</div>
          </div>
        </div>

        {/* Query History List */}
        <div className="bg-white rounded-xl border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-gray-900">Recent Queries</h3>
              <input
                type="text"
                placeholder="Search queries..."
                className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-blue-500"
              />
            </div>
          </div>
          <div className="divide-y divide-gray-200">
            {queryHistory.map(query => (
              <div key={query.id} className="px-6 py-4 hover:bg-gray-50 transition-colors">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      <span className="font-medium text-gray-900">{query.user}</span>
                      <span className="text-xs text-gray-400">•</span>
                      <span className="text-xs text-gray-500">{query.timestamp}</span>
                      {query.assistant && (
                        <>
                          <span className="text-xs text-gray-400">•</span>
                          <span className="text-xs bg-purple-50 text-purple-700 px-2 py-0.5 rounded-full font-medium">
                            {query.assistant}
                          </span>
                        </>
                      )}
                    </div>
                    <p className="text-gray-700 mb-2">{query.query}</p>
                    <div className="flex items-center space-x-3">
                      <div className="flex items-center space-x-1">
                        {query.sources.map((source, i) => (
                          <span key={i} className="text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded-full">
                            {source}
                          </span>
                        ))}
                      </div>
                      <span className="text-xs text-gray-500">{query.tokens} tokens</span>
                    </div>
                  </div>
                  <button className="p-2 hover:bg-gray-100 rounded-lg">
                    <Download className="w-4 h-4 text-gray-600" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  // Billing View
  const BillingView = () => (
    <div className="flex-1 overflow-y-auto bg-gray-50 p-6">
      <div className="max-w-6xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Billing & Usage</h2>
            <p className="text-gray-600 mt-1">Monitor your subscription and usage</p>
          </div>
          <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all">
            Upgrade Plan
          </button>
        </div>

        {/* Current Plan */}
        <div className="bg-gradient-to-br from-blue-600 to-purple-600 rounded-xl p-6 text-white mb-6">
          <div className="flex justify-between items-start">
            <div>
              <div className="text-sm opacity-90 mb-1">Current Plan</div>
              <div className="text-3xl font-bold mb-2">{billingData.currentPlan}</div>
              <div className="text-sm opacity-90">Next billing: {billingData.nextBilling}</div>
            </div>
            <div className="text-right">
              <div className="text-sm opacity-90 mb-1">Monthly Cost</div>
              <div className="text-3xl font-bold">${billingData.currentBill}</div>
            </div>
          </div>
        </div>

        {/* Usage Stats */}
        <div className="grid grid-cols-2 gap-6 mb-6">
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-900">Query Usage</h3>
              <MessageSquare className="w-5 h-5 text-blue-600" />
            </div>
            <div className="mb-2">
              <div className="flex justify-between text-sm mb-1">
                <span className="text-gray-600">Queries this month</span>
                <span className="font-semibold text-gray-900">
                  {billingData.monthlyQueries.toLocaleString()} / {billingData.queryLimit.toLocaleString()}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-blue-600 h-2 rounded-full transition-all"
                  style={{ width: `${(billingData.monthlyQueries / billingData.queryLimit) * 100}%` }}
                ></div>
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              {Math.floor((1 - billingData.monthlyQueries / billingData.queryLimit) * 100)}% remaining
            </p>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-900">Storage Usage</h3>
              <Database className="w-5 h-5 text-purple-600" />
            </div>
            <div className="mb-2">
              <div className="flex justify-between text-sm mb-1">
                <span className="text-gray-600">Storage used</span>
                <span className="font-semibold text-gray-900">
                  {billingData.storage} GB / {billingData.storageLimit} GB
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-purple-600 h-2 rounded-full transition-all"
                  style={{ width: `${(billingData.storage / billingData.storageLimit) * 100}%` }}
                ></div>
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              {Math.floor((1 - billingData.storage / billingData.storageLimit) * 100)}% remaining
            </p>
          </div>
        </div>

        {/* Usage Chart Placeholder */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
          <h3 className="font-semibold text-gray-900 mb-4">Usage Trends</h3>
          <div className="h-64 flex items-center justify-center bg-gray-50 rounded-lg">
            <div className="text-center">
              <BarChart3 className="w-12 h-12 text-gray-400 mx-auto mb-2" />
              <p className="text-gray-500">Usage chart visualization</p>
            </div>
          </div>
        </div>

        {/* Payment Method */}
        <div className="grid grid-cols-2 gap-6">
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h3 className="font-semibold text-gray-900 mb-4">Payment Method</h3>
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
              <div className="flex items-center space-x-3">
                <CreditCard className="w-8 h-8 text-gray-400" />
                <div>
                  <div className="font-semibold text-gray-900">{billingData.paymentMethod}</div>
                  <div className="text-sm text-gray-500">Expires 12/2025</div>
                </div>
              </div>
              <button className="text-blue-600 text-sm font-semibold hover:text-blue-700">
                Edit
              </button>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h3 className="font-semibold text-gray-900 mb-4">Quick Actions</h3>
            <div className="space-y-2">
              <button className="w-full px-4 py-2 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-all text-sm font-medium text-left">
                Download Invoice
              </button>
              <button className="w-full px-4 py-2 bg-gray-50 text-gray-700 rounded-lg hover:bg-gray-100 transition-all text-sm font-medium text-left">
                Update Billing Info
              </button>
              <button className="w-full px-4 py-2 bg-gray-50 text-gray-700 rounded-lg hover:bg-gray-100 transition-all text-sm font-medium text-left">
                View Payment History
              </button>
            </div>
          </div>
        </div>

        {/* Usage Alert */}
        <div className="mt-6 bg-yellow-50 border border-yellow-200 rounded-xl p-6">
          <div className="flex items-start space-x-3">
            <AlertCircle className="w-6 h-6 text-yellow-600 flex-shrink-0" />
            <div>
              <h4 className="font-semibold text-yellow-900 mb-1">Usage Alert</h4>
              <p className="text-sm text-yellow-800">
                You're at {Math.floor((billingData.monthlyQueries / billingData.queryLimit) * 100)}% of your query limit. 
                Consider upgrading to avoid service interruption.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Settings View
  const SettingsView = () => (
    <div className="flex-1 overflow-y-auto bg-gray-50 p-6">
      <div className="max-w-4xl mx-auto">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Settings</h2>

        <div className="space-y-6">
          {/* Organization Settings */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h3 className="font-semibold text-gray-900 mb-4">Organization Settings</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Organization Name</label>
                <input
                  type="text"
                  value={currentOrg.name}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Website</label>
                <input
                  type="text"
                  placeholder="https://acme.com"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
                />
              </div>
            </div>
          </div>

          {/* AI Settings */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h3 className="font-semibold text-gray-900 mb-4">AI Configuration</h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-medium text-gray-900">Enable Voice Input</div>
                  <div className="text-sm text-gray-500">Allow users to ask questions using voice</div>
                </div>
                <button className="relative inline-flex h-6 w-11 items-center rounded-full bg-blue-600">
                  <span className="inline-block h-4 w-4 transform rounded-full bg-white transition translate-x-6"></span>
                </button>
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-medium text-gray-900">Auto-suggest Questions</div>
                  <div className="text-sm text-gray-500">Show suggested follow-up questions</div>
                </div>
                <button className="relative inline-flex h-6 w-11 items-center rounded-full bg-blue-600">
                  <span className="inline-block h-4 w-4 transform rounded-full bg-white transition translate-x-6"></span>
                </button>
              </div>
            </div>
          </div>

          {/* Security */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h3 className="font-semibold text-gray-900 mb-4">Security</h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-medium text-gray-900">Two-Factor Authentication</div>
                  <div className="text-sm text-gray-500">Add an extra layer of security</div>
                </div>
                <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm">
                  Enable
                </button>
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-medium text-gray-900">Session Timeout</div>
                  <div className="text-sm text-gray-500">Auto-logout after inactivity</div>
                </div>
                <select className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-blue-500">
                  <option>30 minutes</option>
                  <option>1 hour</option>
                  <option>2 hours</option>
                  <option>Never</option>
                </select>
              </div>
            </div>
          </div>

          <button className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all font-semibold">
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );

  const ACLDemoView = () => {
    const [authToken, setAuthToken] = useState(null);
    const [currentUser, setCurrentUser] = useState(null);
    const [demoUsers, setDemoUsers] = useState([]);
    const [dataSources, setDataSources] = useState([]);
    const [selectedDataSourceId, setSelectedDataSourceId] = useState(null);
    const [selectedDataSource, setSelectedDataSource] = useState(null);
    const [chatMessages, setChatMessages] = useState([]);
    const [chatInput, setChatInput] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
      loadDemoUsers();
    }, []);

    const loadDemoUsers = async () => {
      try {
        const response = await fetch('http://localhost:3007/api/demo/users');
        const users = await response.json();
        setDemoUsers(users);
      } catch (error) {
        console.error('Failed to load demo users:', error);
      }
    };

    const loginAsUser = async (username) => {
      try {
        const response = await fetch('http://localhost:3007/api/auth/login', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ username })
        });

        if (!response.ok) {
          throw new Error('Login failed');
        }

        const data = await response.json();
        setAuthToken(data.access_token);
        setCurrentUser(data.user);

        await loadDataSources(data.access_token);
      } catch (error) {
        alert('Login failed: ' + error.message);
      }
    };

    const loadDataSources = async (token) => {
      try {
        const response = await fetch('http://localhost:3007/api/data-sources', {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (!response.ok) {
          throw new Error('Failed to load data sources');
        }

        const data = await response.json();
        setDataSources(data.data_sources);
      } catch (error) {
        console.error('Failed to load data sources:', error);
      }
    };

    const logout = () => {
      setAuthToken(null);
      setCurrentUser(null);
      setDataSources([]);
      setSelectedDataSourceId(null);
      setSelectedDataSource(null);
      setChatMessages([]);
    };

    const selectDataSource = (dataSourceId) => {
      const ds = dataSources.find(d => d.id === dataSourceId);
      setSelectedDataSourceId(dataSourceId);
      setSelectedDataSource(ds);
      setChatMessages([]);
    };

    const sendMessage = async () => {
      if (!chatInput.trim() || !selectedDataSourceId || !authToken) return;

      const userMessage = { role: 'user', content: chatInput, timestamp: new Date() };
      setChatMessages(prev => [...prev, userMessage]);
      const currentInput = chatInput;
      setChatInput('');
      setIsLoading(true);

      try {
        const response = await fetch(`http://localhost:3007/api/assistants/${selectedDataSourceId}/chat`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
          },
          body: JSON.stringify({
            message: currentInput,
            conversationHistory: []
          })
        });

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.detail || 'Chat request failed');
        }

        const data = await response.json();
        const botMessage = {
          role: 'assistant',
          content: data.answer,
          citations: data.citations,
          timestamp: new Date()
        };
        setChatMessages(prev => [...prev, botMessage]);

      } catch (error) {
        const errorMessage = {
          role: 'error',
          content: 'Error: ' + error.message,
          timestamp: new Date()
        };
        setChatMessages(prev => [...prev, errorMessage]);
      } finally {
        setIsLoading(false);
      }
    };

    const getRoleColor = (role) => {
      const colors = {
        admin: 'bg-red-100 text-red-800',
        manager: 'bg-blue-100 text-blue-800',
        analyst: 'bg-green-100 text-green-800',
        guest: 'bg-gray-100 text-gray-800'
      };
      return colors[role] || 'bg-gray-100 text-gray-800';
    };

    const getDataSourceTypeColor = (type) => {
      const colors = {
        document_folder: 'bg-blue-100 text-blue-800',
        cloud_storage: 'bg-purple-100 text-purple-800',
        database: 'bg-green-100 text-green-800',
        web_crawler: 'bg-orange-100 text-orange-800'
      };
      return colors[type] || 'bg-gray-100 text-gray-800';
    };

    return (
      <div className="flex-1 flex flex-col overflow-hidden">
        <div className="flex-1 overflow-y-auto p-6">
          <div className="max-w-6xl mx-auto space-y-6">
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h1 className="text-2xl font-bold text-gray-900 mb-6">ACL Demo - Knowledge AI System</h1>

              {/* Authentication Section */}
              <div className="mb-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Authentication</h2>

                {currentUser ? (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="font-medium">Logged in as: </span>
                        <span className="font-semibold">{currentUser.username}</span>
                        <span className={`ml-2 px-2 py-1 rounded-full text-xs font-medium ${getRoleColor(currentUser.role)}`}>
                          {currentUser.role}
                        </span>
                      </div>
                      <button
                        onClick={logout}
                        className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                      >
                        Logout
                      </button>
                    </div>
                  </div>
                ) : (
                  <div>
                    <h3 className="text-md font-medium text-gray-700 mb-3">Demo Users (Click to login - no password needed)</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                      {demoUsers.map(user => (
                        <div
                          key={user.id}
                          onClick={() => loginAsUser(user.username)}
                          className="p-4 bg-gray-50 rounded-lg border border-gray-200 cursor-pointer hover:bg-blue-50 hover:border-blue-300 transition-colors"
                        >
                          <div className="font-semibold text-gray-900">{user.username}</div>
                          <div className="text-sm text-gray-600">{user.email}</div>
                          <span className={`inline-block mt-2 px-2 py-1 rounded-full text-xs font-medium ${getRoleColor(user.role)}`}>
                            {user.role}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Data Sources Section */}
              {currentUser && (
                <div className="mb-6">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4">Your Accessible Data Sources</h2>
                  {dataSources.length === 0 ? (
                    <p className="text-gray-600">No data sources accessible to you.</p>
                  ) : (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                      {dataSources.map(ds => (
                        <div key={ds.id} className="border border-gray-200 rounded-lg p-4">
                          <div className="flex items-start justify-between mb-3">
                            <h3 className="font-semibold text-gray-900">
                              {ds.name} {ds.is_owner && <span className="text-sm text-blue-600">(Owner)</span>}
                            </h3>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getDataSourceTypeColor(ds.type)}`}>
                              {ds.type.replace('_', ' ')}
                            </span>
                          </div>

                          <p className="text-sm text-gray-600 mb-3">{ds.description || 'No description'}</p>

                          <div className="mb-3">
                            <span className="font-medium text-sm">Your Permissions: </span>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {ds.permissions.map(permission => (
                                <span key={permission} className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs font-medium">
                                  {permission}
                                </span>
                              ))}
                            </div>
                          </div>

                          <div className="mb-3">
                            <span className="font-medium text-sm">Capabilities: </span>
                            <div className="flex flex-wrap gap-1 mt-1">
                              <span className={`px-2 py-1 rounded text-xs font-medium ${ds.capabilities.supports_upload ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                {ds.capabilities.supports_upload ? 'Upload' : 'No Upload'}
                              </span>
                              <span className={`px-2 py-1 rounded text-xs font-medium ${ds.capabilities.supports_download ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                {ds.capabilities.supports_download ? 'Download' : 'No Download'}
                              </span>
                              <span className={`px-2 py-1 rounded text-xs font-medium ${ds.capabilities.supports_real_time ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                {ds.capabilities.supports_real_time ? 'Real-time' : 'No Real-time'}
                              </span>
                            </div>
                          </div>

                          {ds.capabilities.file_types_supported.length > 0 && (
                            <p className="text-xs text-gray-500">
                              Supported files: {ds.capabilities.file_types_supported.join(', ')}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Chat Testing Section */}
              {currentUser && dataSources.length > 0 && (
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 mb-4">Chat Testing</h2>

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Select Data Source:</label>
                    <select
                      value={selectedDataSourceId || ''}
                      onChange={(e) => selectDataSource(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
                    >
                      <option value="">Choose a data source...</option>
                      {dataSources.filter(ds => ds.permissions.includes('chat')).map(ds => (
                        <option key={ds.id} value={ds.id}>{ds.name}</option>
                      ))}
                    </select>
                  </div>

                  {selectedDataSource && (
                    <div className="border border-gray-200 rounded-lg p-4">
                      <h3 className="font-semibold text-gray-900 mb-2">Chat with: {selectedDataSource.name}</h3>
                      <div className="mb-4">
                        <span className="text-sm font-medium">Your permissions: </span>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {selectedDataSource.permissions.map(permission => (
                            <span key={permission} className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs font-medium">
                              {permission}
                            </span>
                          ))}
                        </div>
                      </div>

                      <div className="bg-gray-50 rounded-lg p-4 h-64 overflow-y-auto mb-4">
                        {chatMessages.map((message, index) => (
                          <div key={index} className={`mb-3 p-3 rounded-lg ${
                            message.role === 'user' ? 'bg-blue-100 ml-8' :
                            message.role === 'error' ? 'bg-red-100 mr-8' : 'bg-white mr-8'
                          }`}>
                            <div className="text-sm font-medium mb-1">
                              {message.role === 'user' ? 'You' : message.role === 'error' ? 'Error' : 'Assistant'}
                            </div>
                            <div className="text-sm">{message.content}</div>
                            {message.citations && message.citations.length > 0 && (
                              <div className="mt-2 text-xs text-gray-600">
                                Sources: {message.citations.map(c => c.source).join(', ')}
                              </div>
                            )}
                          </div>
                        ))}
                        {isLoading && (
                          <div className="bg-white mr-8 p-3 rounded-lg">
                            <div className="text-sm font-medium mb-1">Assistant</div>
                            <div className="text-sm text-gray-500">Thinking...</div>
                          </div>
                        )}
                      </div>

                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={chatInput}
                          onChange={(e) => setChatInput(e.target.value)}
                          onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && sendMessage()}
                          placeholder="Ask a question..."
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
                          disabled={isLoading}
                        />
                        <button
                          onClick={sendMessage}
                          disabled={isLoading || !chatInput.trim()}
                          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 transition-colors"
                        >
                          <Send className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="h-screen flex overflow-hidden bg-gray-100">
      <Sidebar />
      {currentView === 'chat' && <ChatView />}
      {currentView === 'assistants' && <AssistantsView />}
      {currentView === 'sources' && <DataSourcesView />}
      {currentView === 'workflows' && <WorkflowsView />}
      {currentView === 'users' && <UsersView />}
      {currentView === 'access' && <UserAccessManagement />}
      {currentView === 'history' && <QueryHistoryView />}
      {currentView === 'billing' && <BillingView />}
      {currentView === 'settings' && <SettingsView />}
    </div>
  );
}
